/**
 * src/pages/marketing/RenFestSelfie.jsx
 * ═══════════════════════════════════════════════════════════════════════════
 * THE GUEST WIZARD — one screen, one decision, one tap.
 *
 * ⚠️ READ `selfie-platform/DESIGN-FUNDAMENTALS.md` BEFORE CHANGING THIS FILE.
 * Every structural choice here is one of those laws, and each was paid for by a
 * real person failing at a real screen.
 *
 * ── THE LAWS THIS FILE IS THE IMPLEMENTATION OF ───────────────────────────
 *
 *  A1  One screen, one decision. Splitting a flow into more steps does NOT make
 *      it feel longer (Baymard: perceived effort drives abandonment, not step
 *      count).
 *
 *  A2  ⚠️ NOTHING IMPORTANT IS EVER BELOW THE FOLD. The previous version was a
 *      settings panel with the capture button underneath it, which produced
 *      three of NN/g's documented "Illusion of Completeness" triggers at once —
 *      a viewport-filling element, a hard horizontal edge, and empty space
 *      below. A coworker never found the button. NN/g measured six of eight
 *      users failing to scroll past exactly this pattern.
 *      If a step does not fit one viewport, SPLIT THE STEP. Never add a scroll
 *      hint and call it fixed.
 *
 *  A3  Auto-advance on single-select. Tapping the scene IS the next button.
 *      The gap between "I picked it" and "now what?" is where people are lost.
 *
 *  A4  The primary action is the biggest thing on screen, in the thumb zone.
 *
 *  A7  If a choice needs explaining it is not a guest choice. The front/rear
 *      camera selector is GONE — see below. Style, banner and prompt text moved
 *      to the operator settings (boothSettings.js).
 *
 *  A8  Nothing navigates away while a job is in flight.
 *
 *  A13 Consent is captured where the individual is present: once at the start,
 *      and again per person on the group ready button.
 *
 * ── ⚠️ THERE IS NO CAMERA SELECTOR, AND THERE MUST NOT BE ─────────────────
 * It existed for exactly one scenario: somebody else photographing the group.
 * Group mode removed that scenario — each person now takes their own face shot
 * in turn on the FRONT camera. The rear camera is also actively worse here: the
 * preview is not mirrored so people cannot frame themselves, and the framing
 * halo becomes useless because they cannot see the ring they need to fill.
 * (And per the camera research doc, field of view was never portably
 * controllable anyway.)
 *
 * ⚠️ Generation is a BACKGROUND function. We POST, then poll. Do not
 * "simplify" this into a single synchronous call — Netlify's gateway dies at
 * ~26s and generation is 30-140s.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Camera, Share2, Star, Clock, DollarSign, ArrowLeft,
  AlertTriangle, Sparkles, Crown, Timer, Users, User, RotateCcw, ChevronDown,
} from 'lucide-react';
import EmailCapture from '../../components/features/renfest/emailCapture';
import PrintUpgrade from '../../components/features/renfest/printUpgrade';
import { getPlacement } from '../../lib/placement';
import { getDeviceId } from '../../lib/deviceId';
import { hasOperatorKey } from '../../lib/operator';
import { useTheme } from '../../context/ThemeContext';
import { api } from '../../lib/api';
import { GOLD, buzz } from '../../components/features/renfest/boothChrome';
import {
  say, sayWatched, hush, unlockCues, cueReport, HapticButton,
} from '../../components/features/renfest/boothCues';
import {
  loadBoothSettings, CONSENT_VERSION, CONSENT_TEXT, CONSENT_SUBTEXT,
} from '../../components/features/renfest/boothSettings';
import { loadTestSession, endTestSession } from '../../lib/testSession';
import { THEME } from '../../lib/boothTheme';

/* ─────────────────────────────────────────────────────────── config ─── */

/* ⚠️ RENAMED, NOT CHANGED (2026-10-03). The list is byte-for-byte what it was;
   `SCENES` below is this list unless the event has its own costume. See
   src/lib/boothTheme.js. */
const RENFEST_SCENES = [
  { k: 'dragon',  e: '\u{1F409}', n: 'Slay the Dragon' },
  { k: 'castle',  e: '\u{1F3F0}', n: 'The Castle' },
  { k: 'joust',   e: '\u{1F434}', n: 'The Joust' },
  { k: 'mermaid', e: '\u{1F9DC}', n: 'The Mermaids' },
  { k: 'fairy',   e: '\u{1F9DA}', n: 'The Fairies' },
  { k: 'camel',   e: '\u{1F42A}', n: 'Ride the Camel' },
  { k: 'turkey',  e: '\u{1F357}', n: 'Turkey Leg' },
  { k: 'dungeon', e: '\u{1F3F4}', n: 'The Dungeon' },
  /* ⚠️ LAST ON PURPOSE, AND NOT BECAUSE IT IS LEAST GOOD. `P14` will rank
     these by what guests actually pick; until then the order is the order they
     were built, and a new scene jumping the queue would quietly bias the very
     data that is meant to decide the order. */
  { k: 'entrance', e: '\u{1F3BA}', n: 'The Grand Entrance' },
];
const SCENES = THEME?.scenes || RENFEST_SCENES;

/**
 * ⚠️ THREE FRAMES SOLO, AND EVERY ONE SHOUTS "SMILE".
 * Portraits made from unsmiling reference frames come back flat — the model is
 * faithful, and a neutral face in is a neutral face out. Nobody smiles at a
 * countdown unless told to, so we tell them in the largest type on screen.
 */
/**
 * ⚠️ FRAME 3 IS A WIDE SHOT AND MUST NOT SHOW THE FRAMING RING.
 *
 * The oval means "fill me with your face" - exactly the wrong instruction for
 * the shot that is supposed to capture the whole person being ridiculous. It
 * was telling people to lean IN on the one frame where we want them to step
 * BACK. `wide: true` swaps the oval for corner brackets, which is the universal
 * camera language for "get it all in", and turns off the proximity halo.
 *
 * That frame is also the one carrying the props. The scotch glass, the spoon,
 * the ice cream and a wedding ring all survived into a finished portrait from
 * it, unprompted - so it is where the personality of the picture comes from,
 * and it is worth two lines of instruction instead of one.
 */
/**
 * ⚠️ FRAME 2 IS A THREE-QUARTER TURN, NOT A PROFILE. THIS IS THE FIX FOR THE
 * WORST MOMENT IN THE SEQUENCE.
 *
 * "Turn thy head to the side" asked the guest to turn away from the only
 * surface carrying instructions, then expected them to know when to look back.
 * They could not: the cue word appeared AFTER they had turned, so the largest
 * type on the screen was shouting SMILE at the side of their head. The frames
 * came back neutral and looking away, which is the single worst reference
 * frame we can hand the model.
 *
 * The audio cue helps and is now wired, but it cannot be the fix, because on a
 * muted iPhone in a field full of noise it does not arrive. So the pose itself
 * changed: a three-quarter turn keeps one eye on the glass, which means the
 * guest never loses the cue, never has to be told to look back, and — this is
 * the free part — gives the model both eyes and a readable nose line. A true
 * profile is the worst angle for likeness anyway; almost nothing that drives
 * facial recognition survives it.
 *
 * `say` is what gets spoken aloud and differs from `n` deliberately: written
 * cues want to be short enough to read in one glance, spoken cues want to be
 * a whole sentence or they sound like a robot barking.
 */
const POSES = [
  {
    n: 'Look straight at the glass',
    say: 'Look straight at the glass',
    cue: 'SMILE!', cueSay: 'Smile!', wide: false,
  },
  {
    n: 'Turn a little - keep one eye on the glass',
    say: 'Turn your head a little, but keep one eye on the glass',
    cue: 'SMILE!', cueSay: 'Smile!', wide: false,
  },
  {
    n: 'Step back - go big! Hold something up',
    say: 'Step back, go big, and hold something up',
    cue: 'BE SILLY!', cueSay: 'Be silly, and smile!', wide: true,
  },
];
const N_FRAMES = POSES.length;

/**
 * ⚠️ GROUP MODE IS ONE FACE SHOT PER PERSON, IN TURN. NOT A GROUP PHOTO.
 * A group photo makes the model segment the faces itself with no signal that
 * they must stay distinct — that is how four friends come back as four
 * siblings. And pixels: a face in a 3-up shot is ~150px across after
 * downsampling; a face filling the ring is ~450px. Nine times the pixels on the
 * input that drives likeness most.
 */
/**
 * ⚠️ STYLE BELONGS TO THE GUEST, NOT THE OPERATOR. I moved it out and that was
 * wrong.
 *
 * The camera selector failed the "if it needs explaining it is not a guest
 * choice" test because it named HARDWARE. Cartoon vs flattering vs true is
 * instantly understandable, it is fun rather than technical, and it is the
 * choice that makes someone feel they made the picture rather than received it.
 * Extra steps are not what costs conversion - decisions that feel like WORK
 * are, and this one does not.
 */
/*
 * ⚠️ `full` IS HIDDEN, NOT DELETED, AND THE REASON IS COMMERCIAL NOT TECHNICAL.
 *
 * On 2026-08-31 the RenFest owners objected to the selfie programme as
 * competing with their caricature artist. The strongest thing feeding that
 * objection was our own vocabulary: this list shipped with an option literally
 * labelled **Caricature**, selected by default, on the screen anyone from the
 * festival would see first. Joel's read, and it is right: *"it's none of their
 * business what our internal documentation says ... we just need to put some
 * lipstick on the pig so it's characterised the way they want to push it."*
 *
 * ⚠️ THE VALUE `full` MUST NEVER CHANGE OR BE REMOVED. It is written on
 * `booth_run.likeness` for every run ever generated, including all 17 of
 * ROUND 1. Deleting it orphans that history and breaks every comparison. The
 * generator still accepts it, the admin can still send it, and it comes back
 * by flipping one flag.
 *
 * **Turned back on 2026-09-08**, third in the list, labelled **"Cartoony"**.
 * Do not restore the word "caricature", and do not promote it above the other
 * two: third position is the least-picked slot and that is deliberate while the
 * festival relationship is what it is.
 *
 * ⚠️ ALL THREE LABELS ARE WRITTEN FOR SOMEBODY WHO WILL NOT READ THEM. Joel,
 * 2026-09-07: *"they barely look at them and read the screen so it needs to be
 * simple ... leaving options for guessing isn't good for the average consumer."*
 * The big line carries the meaning, the small line carries the fair voice. If
 * you ever swap them round, the readable half stops being the load-bearing one.
 */
const STYLES = [
  { v: 'light', e: '\u{1F3A8}', n: 'Flattering', d: 'You, on your best day' },
  { v: 'true',  e: '\u{1F5BC}', n: 'Realistic',  d: 'Painted exactly as you are' },
  { v: 'full',  e: '\u{1F3AD}', n: 'Cartoony',   d: 'Still you, just exaggerated' },
];

/* What the guest actually sees. One filter, one place, so hiding an option can
   never fall out of sync with what the picker renders. */
const VISIBLE_STYLES = STYLES.filter((st) => !st.hidden);

/* ⚠️ AN EVENT CAN TAKE THE STYLE CHOICE AWAY ENTIRELY. Joel, at Retro Night,
   2026-10-03: "remove all the options ... just let it roll with auto selection
   of cartoony." When the theme names a likeness, the style step is skipped and
   every run uses it. Null on RenFest, where the picker is unchanged. The value
   must be one the generator and the booth_run CHECK accept: true, light, full. */
const FORCED_STYLE = THEME?.likeness || null;

/**
 * Below this, the live preview shows "Hold still - it looks blurry". WARNING
 * ONLY - it has never gated the shutter and must not start now: a hint may be
 * wrong, a gate may not.
 *
 * ⚠️ 45 WAS DEAD. The first two real phone runs scored 916 (sharp) and 260 (the
 * run whose third shot came out blurry) on this preview metric - 6x and 20x
 * above the threshold. It could not fire, which is why blur detection "wasn't
 * working" on 2026-08-22. A threshold nothing can cross is not a safety net,
 * it is a comment.
 *
 * ⚠️ 350 IS PROVISIONAL AND BUILT ON TWO DATA POINTS. It sits between the good
 * run and the blurry one, which is the best that two samples allow. Every
 * capture now logs a REAL per-shot score to `client_meta.sharpness`; recalibrate
 * from a few hundred of those, not from this comment.
 */
const BLUR_MIN = 350;

/**
 * Below this, the guest is ASKED whether to retake that shot.
 *
 * ⚠️ THIS IS THE CAPTURE METRIC (`sharpnessOf`), NOT THE PREVIEW ONE
 * (`BLUR_MIN` / `probeFrame`). Different image, different downsample, different
 * numbers. Never copy a value between them.
 *
 * ⚠️ NO REAL DATA EXISTS FOR THIS THRESHOLD YET - the capture metric was only
 * written on 2026-08-22, so nothing has been scored by it in the field. 120 is
 * a deliberately CONSERVATIVE opening bid: it should only fire on obviously bad
 * frames, because a prompt that interrupts a sharp photo is worse than one that
 * misses a soft one.
 *
 * ⚠️ IT CALIBRATES ITSELF, AND THAT IS THE POINT. Every prompt records the
 * score AND what the guest answered into `client_meta.blur_prompts`. A "no, it
 * is fine" is a human-labelled false positive; a run with a blurry shot that
 * was never offered a retake is a false negative visible in `sharpness`. After
 * a few dozen runs the number can be set from evidence instead of judgement.
 * Do not guess at it again - read the log.
 */
const CAPTURE_BLUR_MIN = 120;

/** One retry per shot. A retake loop is a guest who cannot finish. */
const MAX_RETAKES_PER_SHOT = 1;

const MAX_PEOPLE = 6;
const GROUP_CUE = 'SMILE!';

const PACE = { read: 2500, tick: 1000, after: 1200 };

/**
 * ⚠️ NO MODEL PICKER. The tier is a character, not a setting — it makes the
 * wait his fault in a way people find funny rather than broken, and all the
 * workshop patter is his voice.
 */
const PAINTER = THEME?.painter || { name: 'Master Bartholomew Sable', wait: 'about a minute' };
const MODEL_ETA = 66;             // 58s measured + headroom
const MODEL_OUT = 0.049;
const FRAME_IN_COST = 0.0088;

/* ⚠️ REMOVED 2026-08-31 with the filename rewrite. Its only job was to stamp a
   two-character painter code into the guest's download — `kcrenfest-20260831-
   4Wa2b3.png` — which told a guest nothing and told us nothing we cannot read
   off `model_id` on the row. Filenames are named by the server now
   (netlify/functions/_filename.js). If a model code is ever wanted again it
   belongs in a template token, not hardcoded on the guest's phone. */

const POLL_MS = 2000;
const POLL_TIMEOUT_MS = 300000;

/**
 * What a guest is told when the server refuses, keyed by `block_reason`.
 *
 * ⚠️ These are the only words a real person ever sees for a refusal, so they
 * say WHAT HAPPENS NEXT, never what went wrong internally. "The booth is done
 * for today" is actionable. "event_daily_spend" is not.
 */
const BLOCK_MESSAGES = {
  event_hourly_cap: 'The booth is busy right now. Give it a few minutes and try again.',
  event_daily_spend: 'The booth is finished for today. Come back tomorrow!',
  kill_switch: 'The booth is paused. Ask a member of staff.',
};
/**
 * What a guest is told when a run FAILS, keyed by `error_kind`.
 *
 * ⚠️ SAME SPLIT AS BLOCK_MESSAGES ABOVE, AND FOR THE SAME REASON. The server
 * classifies in one word; the words a person reads live here. `booth_run.error`
 * keeps the verbatim provider text for the console's error view, and a guest
 * never sees it: "Your request was rejected by the safety system" is true and
 * helps nobody standing at a festival with a phone in their hand.
 *
 * ⚠️ THE REFUSAL COPY NAMES A CAUSE AND AN ACTION, WITHOUT ACCUSING ANYBODY.
 * Joel held up a Minion on the client call and OpenAI declined it as a
 * trademarked character. The guest did nothing wrong and must not be told they
 * did; they also cannot fix it unless somebody tells them WHAT to change. So:
 * name the prop, not the person, and say what to do next.
 *
 * ⚠️ NULL FALLS THROUGH to the generic line, which is what every run before
 * today already showed. Nothing regresses when a kind is missing.
 */
const ERROR_MESSAGES = {
  /* ⚠️ THE GENERAL CASE NAMES THE LIKELY CAUSE WITHOUT ASSERTING IT, because
     most of the time the provider does not tell us why. OpenAI's image-edit
     refusal is usually the bare sentence "rejected as a result of our safety
     system" with no reason attached. Saying "trademark" to somebody whose real
     trigger was something else is confidently wrong, to a stranger, about their
     own photograph — a worse failure than being unspecific. So: name what it
     usually is, and say what to do.

     ⚠️ "TAKE YOUR PHOTOS AGAIN", NEVER "TAKE THREE MORE". Joel, 2026-09-03:
     "if someone is doing a photo with multiple people, it's only a single photo
     per person." A solo guest takes three; a group of five takes five. Any
     copy that names a number is wrong for most of the groups that walk up to
     this booth, and being wrong about what the booth just asked them to do is
     how a guest decides the thing is broken.

     ⚠️ AND "NOTHING IS WRONG ON YOUR END", not "with your photo" — same reason.
     A group has photos, plural, and one of them may be the problem. */
  refused: 'The painter would not paint that one. This is almost always a character, costume or logo it is not allowed to copy — a toy, a mask, a brand on a shirt. Set the prop aside and take your photos again.',
  refused_ip: 'The painter cannot copy a trademarked or copyrighted character. Nothing is wrong on your end — set the toy, mask or logo aside and take your photos again.',
  refused_person: 'The painter cannot paint a likeness of a real public figure. Take your photos again with just your own faces.',
  refused_adult: 'The painter would not paint that one. Keep it family friendly for the fair and take your photos again.',
  refused_violence: 'The painter would not paint that one. Set any weapon or prop aside and take your photos again.',
  refused_minor: 'The painter would not paint that one. Ask a member of staff and they will sort it out.',
  storage: 'That one did not save. Take your photos again.',
};

const PARTIAL_TARGET = 3;

/* ⚠️ THE FRAMED COMPOSITE ARRIVES AFTER "done", AND THAT IS BY DESIGN.
   Compositing runs once the run is already marked finished and its errors are
   swallowed, so a bug in the frame degrades to "no frame" and never to "no
   portrait" (`D-49`). Measured at ~2.5-3.5s. These bound the wait for it:
   ten tries at 1.5s is fifteen seconds, comfortably past the measured time and
   short enough that a failed composite stops costing requests. Running out is
   not an error — the guest keeps the clean portrait. */
/* ⚠️ THESE ARE SET FROM MEASURED PRODUCTION TIMES, NOT FROM THE ESTIMATE.
   Ten real runs on the deployed function: the composite lands 7.8s-9.2s after
   the run is marked done, mean 8.2s. The old values here (1.5s x 10 = 15s) came
   from a 2.5s container measurement and left barely five seconds of headroom.

   So: do not ask at all for the first five seconds — the file cannot exist yet
   and every early poll is a wasted request on festival LTE — then ask every
   1.2s for 25s. Total window ~30s, which is three times the observed spread. */
const FRAMED_FIRST_DELAY_MS = 5000;
const FRAMED_POLL_MS = 1200;
const FRAMED_TRIES = 21;

const RENFEST_PATTER = [
  'STRETCHING THE CANVAS', 'HUZZAH! A FINE FACE!', 'GRINDING LAPIS FOR THE BLUE',
  'OH, THAT WAS A GOOD ONE', 'HE SQUINTS AT THY NOSE', 'MORE ALE FOR THE ARTIST!',
  'MIXING OCHRE AND BONE BLACK', 'A PERFECT TAKE, MILORD',
  'PAINTING THINE EYES - DELICATE WORK', 'MIND THY TURKEY LEG',
  'HE IS BEING TERRIBLY FINICKY', 'THE LIKENESS IS UNCANNY',
  'HORSEHAIR BRUSH, FINEST GRADE', 'SOMEONE FETCH MORE PIGMENT',
  'THE BEARD, HAIR BY HAIR', 'THY JAW IS MAGNIFICENT',
  'HE STEPS BACK. HE SQUINTS. HE NODS.', 'BEWARE THE JOUSTING LANE',
  'WARMING THE SKIN TONES', 'THE COURT SHALL BE PLEASED',
  'THESE SCALES TAKE PATIENCE', 'A POX UPON SLOW BRUSHES',
  'GOLD LEAF ALONG THE BORDER', 'HE SIGNETH THE CORNER',
];
const RENFEST_OVERTIME_PATTER = [
  'HE IS FUSSING OVER A DETAIL', 'ONE LAST GLAZE, I SWEAR IT',
  'ART CANNOT BE HURRIED', 'ALMOST THINE, GOOD GENTLE',
];
/* Same rule as SCENES above: the RenFest lists are untouched, a themed event
   swaps in its own. */
const PATTER = THEME?.patter || RENFEST_PATTER;
const OVERTIME_PATTER = THEME?.overtimePatter || RENFEST_OVERTIME_PATTER;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const PAYLOAD_BUDGET = 1.2 * 1024 * 1024;
const b64Bytes = (d) => d.reduce((n, x) => n + x.length, 0);
const humanKB = (n) => `${Math.round(n / 1024)} KB`;
const dataUrlToBlob = (d) => fetch(d).then((r) => r.blob());
const secs = (ms) => (ms == null ? '-' : `${(ms / 1000).toFixed(1)}s`);
const usd = (n) => (n == null ? '-' : `$${Number(n).toFixed(3)}`);
const newJobId = () => `rfs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
/** Bake-off cards are named, never numbered - a number reads as a ranking. */
const LETTERS = 'ABCDEFGHJKLMNPQRSTUV'.split('');   // no I or O - they read as 1 and 0

function reencode(dataUrl, maxW, quality) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width);
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

async function fitToBudget(frames, budget = PAYLOAD_BUDGET) {
  const LADDER = [{ w: 900, q: 0.82 }, { w: 768, q: 0.75 }, { w: 768, q: 0.62 },
    { w: 640, q: 0.62 }, { w: 512, q: 0.55 }];
  let current = frames;
  let note = 'original';
  if (b64Bytes(current) <= budget) return { frames: current, note: `original ${humanKB(b64Bytes(current))}` };
  for (const step of LADDER) {
    current = await Promise.all(frames.map((f) => reencode(f, step.w, step.q)));
    note = `${step.w}px q${step.q} -> ${humanKB(b64Bytes(current))}`;
    if (b64Bytes(current) <= budget) return { frames: current, note };
  }
  return { frames: current, note: `${note} (still over budget)` };
}

/**
 * ⚠️ THE SERVER NAMES THE FILE NOW — `download_name` on the status response,
 * built from the event's own template (sql/040, netlify/functions/_filename.js).
 *
 * This used to produce `kcrenfest-<date>-<modelcode><4 chars of job id>.png`.
 * Unique, but meaningless to the person holding it, and `kcrenfest` was
 * hardcoded so no second client could ever have used it. Meanwhile
 * `delivery.js` named the SAME portrait `scene-twist-<date>.jpg` — identical
 * for every guest that day, which is what a phone answers with "(1)", "(2)".
 * Three implementations, three answers, one picture.
 *
 * The fallback below is for a cached older response with no `download_name`,
 * and carries the claim code because that is the part a guest can act on: they
 * can read it off the filename at booth #383 and be found.
 */
function portraitFilename(run) {
  if (run?.download_name) return run.download_name;
  const code = String(run?.claim_code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return `scenetwist-${code || String(run?.job_id || 'portrait').slice(-6)}.png`;
}

async function fetchAsFile(url, filename) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not fetch the image (${res.status})`);
  const blob = await res.blob();
  return { blob, file: new File([blob], filename, { type: blob.type || 'image/png' }) };
}

/**
 * ⚠️ DOWNLOAD IS PRIMARY AND UNCONDITIONAL; SHARE IS SECONDARY.
 * `<a href={signedUrl} download>` does NOT work — `download` is ignored
 * cross-origin, so the browser navigates to the image, which on a phone means a
 * bare PNG in a new tab with no route back AND a dead poll. And `canShare`
 * proves the API exists, not that the OS sheet offers any way to save.
 */
async function downloadPortrait(url, filename) {
  const { blob } = await fetchAsFile(url, filename);
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl; a.download = filename; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(objUrl), 15000);
}

async function sharePortrait(url, filename) {
  const { file } = await fetchAsFile(url, filename);
  if (!navigator.canShare?.({ files: [file] })) throw new Error('Sharing is not available on this device.');
  await navigator.share({ files: [file], title: THEME?.shareTitle || 'KC RenFest 2026' });
}

const canShareFiles = () => {
  try { return !!navigator.canShare?.({ files: [new File([new Blob()], 'a.png', { type: 'image/png' })] }); }
  catch { return false; }
};

/**
 * ⚠️ HEURISTIC, NOT FACE DETECTION. Skin-tone coverage inside the guide oval.
 * Lighting-sensitive, cannot tell a face from a bare arm — fine, because all it
 * drives is a glow. A HINT MAY BE WRONG; A GATE MAY NOT. It never gates the
 * shutter.
 */
function probeFrame(video, work) {
  if (!video?.videoWidth) return { skin: 0, sharp: 0 };
  const W = 72, H = 96;
  work.width = W; work.height = H;
  const ctx = work.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(video, 0, 0, W, H);
  const { data } = ctx.getImageData(0, 0, W, H);

  /* ── framing: skin-tone coverage inside the guide oval (Kovac's rule) ──
     ⚠️ A HINT, NOT FACE DETECTION. Lighting-sensitive, cannot tell a face from
     a bare arm. That is fine because all it drives is a glow, and it must never
     gate the shutter. */
  let inOval = 0, skin = 0;
  const cx = W / 2, cy = H * 0.44, rx = W * 0.30, ry = H * 0.30;

  /* ── sharpness: variance of the Laplacian ──
     The classic cheap blur metric. Convolve grayscale with a 3x3 Laplacian and
     take the variance of the response: an in-focus frame has strong edges and
     high variance, a blurred one has almost none. Runs on 6,912 pixels every
     300ms, which is nothing.
     ⚠️ THE THRESHOLD IS A GUESS AND IS DELIBERATELY ONLY A WARNING. Absolute
     values depend on the sensor, the lighting and this exact downsample size.
     Every score is logged with the run so it can be calibrated against real
     festival photographs before it is ever allowed to block anyone. */
  const gray = new Float32Array(W * H);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }

  let sum = 0, sumSq = 0, n = 0;
  for (let y = 1; y < H - 1; y += 1) {
    for (let x = 1; x < W - 1; x += 1) {
      const p = y * W + x;
      const lap = 4 * gray[p] - gray[p - 1] - gray[p + 1] - gray[p - W] - gray[p + W];
      sum += lap; sumSq += lap * lap; n += 1;

      const dx = (x - cx) / rx, dy = (y - cy) / ry;
      if (dx * dx + dy * dy <= 1) {
        inOval += 1;
        const i4 = p * 4;
        const r = data[i4], g = data[i4 + 1], b = data[i4 + 2];
        const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        if (r > 95 && g > 40 && b > 20 && mx - mn > 15 && Math.abs(r - g) > 15 && r > g && r > b) skin += 1;
      }
    }
  }

  const mean = sum / n;
  return {
    skin: inOval ? skin / inOval : 0,
    sharp: Math.round(sumSq / n - mean * mean),
  };
}

/* ─────────────────────────────────────────────────── step shell ─── */

/**
 * Every step renders through this. It is what makes A2 structural rather than a
 * thing we remember: the step body is centred in the viewport and the primary
 * action is pinned to the bottom. There is no scroll to miss.
 */
function Step({ title, sub, children, footer, onBack, dots, dotIndex }) {
  return (
    <div className="rf-step">
      <div className="rf-step-head">
        {onBack ? (
          <button type="button" className="rf-back" onClick={() => { buzz(8); onBack(); }} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
        ) : <span />}
        {dots > 0 && (
          <div className="rf-dots" aria-hidden="true">
            {Array.from({ length: dots }, (_, i) => (
              <span key={i} className={`rf-dot${i === dotIndex ? ' is-on' : ''}`} />
            ))}
          </div>
        )}
        <span />
      </div>

      <div className="rf-step-body">
        {title && <h1 className="rf-ink rf-step-title">{title}</h1>}
        {sub && <p className="rf-ink rf-step-sub">{sub}</p>}
        {children}
      </div>

      {footer && <div className="rf-step-foot">{footer}</div>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════ component ═══ */

export default function RenFestSelfie({ onChrome, onShop, onProgress }) {
  const { dark } = useTheme();

  // step: welcome -> who -> count -> scene -> style -> ready -> capturing -> painting -> done
  const [step, setStep] = useState('welcome');
  const [agreed, setAgreed] = useState(false);
  /* True when the synthesiser refused to start at all — a different problem
     from a phone turned down, and the only one of the two we can detect. */
  const [speechDead, setSpeechDead] = useState(false);
  const consentAtRef = useRef(null);

  const [isGroup, setIsGroup] = useState(false);
  const [people, setPeople]   = useState(2);
  const [scene, setScene]     = useState(null);
  /* ⚠️ WAS 'full', WHICH IS NOW HIDDEN. A default pointing at an option the
     picker does not render means every guest who never touches the control gets
     a style they were not shown and cannot see selected. Defaults must always
     name a VISIBLE option — see the STYLES block above. */
  const [likeness, setLikeness] = useState(FORCED_STYLE || VISIBLE_STYLES[0].v);
  const [extra, setExtra]       = useState('');
  const [showDetail, setShowDetail] = useState(false);

  const [settings] = useState(loadBoothSettings);

  const [frames, setFrames]   = useState([]);
  const [countdown, setCd]    = useState('');
  const [poseText, setPose]   = useState('');
  const [cue, setCue]         = useState('');
  const [wide, setWide]       = useState(false);
  const [waitingFor, setWaitingFor] = useState(0);
  /* { label, url, score } while the retake question is on screen, else null. */
  const [retake, setRetake] = useState(null);
  const [framedOk, setFramed] = useState(false);
  const [sharp, setSharp]     = useState(999);
  const [status, setStatus]   = useState('');
  const [camError, setCamErr] = useState('');
  /* ⚠️ SET ONLY WHEN A RUN THAT WAS ALREADY PAINTING DIES, never by a camera
     that would not start. The difference matters because this is what the
     GLOBAL bar reports: a guest sitting in the Shoppe when the generator fails
     is returned to `ready` silently, and without this they would wait in a shop
     forever for a portrait that stopped existing. A camera error, by contrast,
     is already on the screen they are looking at. */
  const [paintErr, setPaintErr] = useState('');

  const [paintSec, setPaintSec]   = useState(0);
  const [baseEta, setBaseEta]     = useState(MODEL_ETA);
  const [etaS, setEtaS]           = useState(MODEL_ETA);
  const [patterIdx, setPatterIdx] = useState(0);
  /* What this run won, and whether the guest has been shown it yet. The draw
     already happened server-side at claim time (D-54); the booth only reveals. */
  const [prize, setPrize] = useState(null);
  const [prizeShown, setPrizeShown] = useState(false);
  const [bgIdx, setBgIdx]         = useState(0);
  const [partial, setPartial]     = useState({ url: null, index: null, count: 0 });

  const [result, setResult] = useState(null);
  /* ⚠️ READ ONCE, AT MOUNT. A test session is handed over by the console
     immediately before this page opens and does not change under us; re-reading
     it every render would let a session that expires mid-capture change the
     branch the guest is already inside. */
  const testRef = useRef(loadTestSession());
  const [testRuns, setTestRuns] = useState([]);   // [{ job_id, model_id, row }]
  const [revealed, setRevealed] = useState(false);
  const [winner, setWinner] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [rating, setRating] = useState(0);
  const [saving, setSaving] = useState('');
  const [stats, setStats]   = useState([]);
  const [shareable] = useState(canShareFiles);
  /* ⚠️ THE GATE ON SHARE. True only after a send has actually SUCCEEDED —
     EmailCapture throws on failure, so the line after the await never runs on a
     bad address. Joel, 2026-08-27: "we still have a share button that should
     only go on once submitted their email. we need to capture the email first."
     The address is the commercial reason the free path exists; handing over a
     shareable file before we have it trades the whole lead for a tap. */
  const [emailSent, setEmailSent] = useState(false);

  const readyRef  = useRef(null);
  const retakeRef = useRef(null);
  /* Every blur prompt and its answer. This is the calibration set for
     CAPTURE_BLUR_MIN - see the note there. */
  const blurPrompts = useRef([]);
  const videoRef  = useRef(null);

  /**
   * ⚠️ CALLBACK REF, NOT A PLAIN ref={videoRef}. THIS IS LOAD-BEARING.
   *
   * `ready` and `capturing` are separate branches of the tree, so React
   * UNMOUNTS the <video> and mounts a brand new node when the step changes.
   * A plain ref points at the new node but `srcObject` is still only set on the
   * dead one — the preview goes black the instant capture starts, every frame
   * comes back empty, and the run dies with "nothing captured".
   *
   * That is exactly what shipped. This reattaches the live stream to whatever
   * node React hands us, every time, so remounting is harmless.
   */
  // startCamera is defined before reportFailure, so it reaches it through a ref
  // rather than forcing a declaration-order shuffle.
  /* ⚠️ HOW MANY TIMES THIS GUEST HAS REACHED `ready` IN THIS SESSION.
     1 is a first run. 2 or more means they came back after something went
     wrong, which is the single most useful fact about a client-side failure and
     the one the 0x0 report on 2026-09-03 was missing. Now that refused guests
     are told to take their photos again, this is a path we send people down on
     purpose and should be able to see. Declared here, above `reportFailure`,
     because that is where it is read. */
  const attemptRef = useRef(0);
  const reportFailureRef = useRef(null);
  /* ⚠️ TRUE WHILE THE PERMISSION PROMPT IS ON SCREEN. getUserMedia does not
     resolve until the guest taps Allow, and a guest reading a dialog for the
     first time takes far longer than any timeout we would pick. Without this,
     "we are waiting for a human" is indistinguishable from "the camera is
     dead" — see the camera assertion below. */
  const camPendingRef = useRef(false);
  /* True once the assertion has given up. Drives the visible Retry, which is
     the only thing on this screen that can actually rescue a guest. */
  const [camBlocked, setCamBlocked] = useState(false);

  const attachVideo = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current && node.srcObject !== streamRef.current) {
      node.srcObject = streamRef.current;
      node.play?.().catch(() => { /* autoplay policy; muted+playsInline covers it */ });
    }
  }, []);
  const streamRef = useRef(null);
  const workRef   = useRef(null);
  const cancelRef = useRef(false);
  const partialIndexRef = useRef(null);

  const onCamera = step === 'ready' || step === 'capturing';

  /* ⚠️ THE BOOTH BAR IS GONE FROM `ready` ONWARD, not just during capture.
     From the guest's point of view the process is in motion the moment they are
     looking at a camera. That bar is ~60px of Home / Ledger / Shop nobody needs
     while framing their face, and it is what pushed the capture button below
     the fold. Shop access during the wait moved INTO the painting step, where
     it belongs anyway. Navigation mid-run is not a feature, it is a way to lose
     the picture. */
  useEffect(() => {
    onChrome?.(!['ready', 'capturing', 'painting', 'done'].includes(step));
  }, [step, onChrome]);
  const clockOn  = step === 'painting';

  /* ─── camera ──────────────────────────────────────────────────────
     ⚠️ STARTED AT THE `ready` STEP, NEVER ON LOAD. A permission prompt
     before the guest knows why is a permission denial. By this point they
     have agreed, chosen who is in it and picked a scene — they are invested,
     and the viewfinder behind the prompt is the explanation. */
  const startCamera = useCallback(async () => {
    setCamErr('');
    setCamBlocked(false);
    camPendingRef.current = true;
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1920 }, height: { ideal: 1440 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play?.().catch(() => {});
      }
    } catch (e) {
      setCamErr(e.name === 'NotAllowedError'
        ? 'We need the camera to paint you. Allow it in your browser settings, then tap again.'
        : `Camera unavailable: ${e.message}`);
      /* ⚠️ THIS IS THE ROW THAT EXPLAINS EVERYTHING, so it carries the
         exception's own name. `NotAllowedError` (they said no),
         `NotReadableError` (another app has the camera) and `NotFoundError`
         (there isn't one) are three different problems with three different
         answers at a booth, and "camera unavailable" tells you which one it is
         not. */
      reportFailureRef.current?.(newJobId(), 'client_camera', `${e.name}: ${e.message}`);
    } finally {
      camPendingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (onCamera && !streamRef.current) startCamera();
  }, [onCamera, startCamera]);

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  useEffect(() => {
    if (!onCamera || wide) { setFramed(false); return undefined; }
    if (!workRef.current) workRef.current = document.createElement('canvas');
    const id = setInterval(() => {
      try {
        const q = probeFrame(videoRef.current, workRef.current);
        setFramed(q.skin >= 0.18);
        setSharp(q.sharp);
      } catch { /* not-yet-ready frame */ }
    }, 300);
    return () => clearInterval(id);
  }, [onCamera, wide]);

  /**
 * Variance of the Laplacian on a CAPTURED frame.
 *
 * ⚠️ THIS IS NOT THE SAME MEASUREMENT AS `probeFrame`, AND THE DIFFERENCE IS
 * THE WHOLE POINT. `probeFrame` scores a 72x96 downsample of the live preview
 * to drive a glow. Downsampling destroys blur - a shaky frame looks perfectly
 * sharp at 72x96 - so that number can never say anything about the picture
 * that was actually taken. This one runs on the 900x1200 capture, sampled down
 * to 300x400, which keeps enough detail for the metric to mean something.
 *
 * Values from the two are NOT comparable. Do not reuse a threshold across them.
 */
function sharpnessOf(canvas) {
  const W = 300, H = 400;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const x = c.getContext('2d', { willReadFrequently: true });
  x.drawImage(canvas, 0, 0, W, H);
  const { data } = x.getImageData(0, 0, W, H);

  const gray = new Float32Array(W * H);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  let sum = 0, sumSq = 0, n = 0;
  for (let y = 1; y < H - 1; y += 1) {
    for (let xx = 1; xx < W - 1; xx += 1) {
      const p = y * W + xx;
      const lap = 4 * gray[p] - gray[p - 1] - gray[p + 1] - gray[p - W] - gray[p + W];
      sum += lap; sumSq += lap * lap; n += 1;
    }
  }
  const mean = sum / n;
  return Math.round(sumSq / n - mean * mean);
}

const grab = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const W = 900, H = 1200;
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const x = c.getContext('2d');
    x.translate(W, 0); x.scale(-1, 1);            // front camera: un-mirror
    const vr = v.videoWidth / v.videoHeight, tr = W / H;
    let sw, sh, sx, sy;
    if (vr > tr) { sh = v.videoHeight; sw = sh * tr; sx = (v.videoWidth - sw) / 2; sy = 0; }
    else         { sw = v.videoWidth;  sh = sw / tr; sx = 0; sy = (v.videoHeight - sh) / 2; }
    x.drawImage(v, sx, sy, sw, sh, 0, 0, W, H);
    /* Measured HERE, on this exact frame, not read from a React state value
       set by a background interval - see the note on sharpScores. */
    let sharpness = null;
    try { sharpness = sharpnessOf(c); } catch { /* never let a metric lose a shot */ }
    return { dataUrl: c.toDataURL('image/jpeg', 0.92), sharpness };
  }, []);

  /* ─── learned ETA ─────────────────────────────────────────────────
     ⚠️ The countdown must come from THIS deployment's measured history. A
     hardcoded one was wrong by 72 seconds on a better model, ran out, and
     left a dead clock — which reads as "it crashed", not "nearly done". */
  useEffect(() => {
    api.renfestSelfie.runs(1)
      .then((d) => setStats(d.stats || []))
      .catch(() => { /* the seeded ETA is a fine fallback */ });
  }, []);

  const etaFor = useCallback(() => {
    const stat = stats.find((s) => s.model_id === settings.modelId);
    if (!stat?.avg_ms || !stat.ok) return MODEL_ETA;
    return Math.max(10, Math.round((stat.avg_ms / 1000) * 1.15));
  }, [stats, settings.modelId]);

  useEffect(() => {
    if (!clockOn) return undefined;
    const t0 = Date.now();
    const tick = setInterval(() => setPaintSec(Math.round((Date.now() - t0) / 1000)), 250);
    const line = setInterval(() => setPatterIdx((i) => i + 1), 4000);
    const bg   = setInterval(() => setBgIdx((i) => i + 1), 2200);
    return () => { clearInterval(tick); clearInterval(line); clearInterval(bg); };
  }, [clockOn]);

  // Rebases rather than expiring. A timer that hits 0:00 and keeps running
  // reads as broken; one that quietly grants itself another 20s reads as
  // "nearly there" - and by then a partial is usually already reassuring.
  useEffect(() => {
    if (clockOn && paintSec >= etaS) setEtaS((e) => e + 20);
  }, [clockOn, paintSec, etaS]);

  /* ─── volume prompt ───────────────────────────────────────────────
     Speak the moment the step appears, so the guest hears the thing the
     screen is telling them to turn up. Watching onstart is the only part of
     this we can actually measure: if the synthesiser never started, no amount
     of turning the volume up will help and the screen should stop asking. */
  useEffect(() => {
    if (step !== 'volume') return undefined;
    let alive = true;
    /* ⚠️ SINGULAR, AND CONFIRMED BY EAR. "live countdowns" was plural and read
       as clumsy aloud; Joel heard this version on a real run 2026-08-27 and
       approved it. It is spoken by a synthesiser to a stranger in a field, so it
       has to survive being heard once, at a distance, with no second take.
       Do not "improve" it without listening to it on a phone first. */
    sayWatched("Turn your volume up, I'll give you a live countdown.").then((r) => {
      if (alive) setSpeechDead(!r.started);
    });
    return () => { alive = false; };
  }, [step]);

  /* ─── capture ─────────────────────────────────────────────────── */

  /** One place every client-side failure goes. See the note at the capture guard. */
  const reportFailure = useCallback(async (jobId, stg, message, extra = {}) => {
    try {
      await api.renfestSelfie.clientError({
        job_id: jobId, stage: stg, error: message,
        scene: scene || 'unknown',
        likeness, banner: settings.banner,
        model_id: settings.modelId,
        people_count: isGroup ? people : 1,
        client_meta: {
          ua: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          step, trail: trail.current.join(' > '),

          /* ⚠️ EVERY FIELD BELOW ANSWERS A QUESTION WE HAVE ACTUALLY BEEN
             UNABLE TO ANSWER, which is the bar for adding one. Joel,
             2026-09-03: "if we can create some better logging or more details
             to grab things or information from the screen that the person was
             on or what device they were on or what they were doing... I find
             that oftentimes we don't log enough to be able to retroactively go
             back and fix things." Agreed — with the discipline that a field
             nobody would ever query is not telemetry, it is noise that makes
             the useful fields harder to read.

             `camera` — TELEMETRY.md GAP 2 says in as many words that we do not
             record what resolution getUserMedia actually gave us, so every
             sharpness number we have is uninterpretable. This closes it.

             `attempt` — tonight's 0x0 was a RE-ENTRY after a failure, and
             nothing in the payload said so. Without it the trail has to be read
             by eye to notice the step repeats.

             `net` — a festival hotspot is a real variable and we have never
             measured it. When the booth is slow on Saturday this is the first
             thing anybody will want.

             `orient` — layout bugs are overwhelmingly orientation bugs, and a
             viewport string alone does not distinguish a rotated phone from a
             small one. */
          camera: (() => {
            const v = videoRef.current;
            return v?.videoWidth ? `${v.videoWidth}x${v.videoHeight}` : null;
          })(),
          attempt: attemptRef.current,
          net: (() => {
            const c = navigator.connection;
            return c ? { type: c.effectiveType || null, down: c.downlink ?? null, save: !!c.saveData } : null;
          })(),
          orient: window.screen?.orientation?.type || null,

          ...extra,
        },
      });
    } catch (e) { console.error('[renfest-selfie] could not record failure', e); }
  }, [scene, settings, likeness, isGroup, people, step]);

  useEffect(() => { reportFailureRef.current = reportFailure; }, [reportFailure]);

  /* ═══════════════════════════════════════════════════════════════════
     ⚠️ SILENT-FAILURE DETECTION. THIS EXISTS BECAUSE THE WORST BUG SO FAR
     THREW NOTHING.
     The painting canvas collapsed to zero height, every promise resolved,
     the portrait generated fine, and the guest stared at a black screen.
     No exception, no rejected fetch, nothing for a catch block to see.
     Waiting for errors only finds the failures that announce themselves.
     So we ASSERT the things that must be true, and report when they are not.
     ═══════════════════════════════════════════════════════════════════ */

  // Reporting is capped per session. A layout bug fires on every render and a
  // festival full of broken phones must not DDOS our own logging.
  const sharpScores = useRef([]);
  const reportCount = useRef(0);
  const probe = useCallback((stg, message, extra) => {
    if (reportCount.current >= 6) return;
    reportCount.current += 1;
    reportFailureRef.current?.(newJobId(), stg, message, extra);
  }, []);

  /* ASSERTION 1 — the media box has real pixels.
     If the viewfinder or the painting canvas is smaller than a postage stamp
     on a step that is supposed to be showing something, the layout has broken
     and the guest is looking at nothing. */
  useEffect(() => {
    if (!['ready', 'capturing', 'painting', 'done'].includes(step)) return undefined;
    /* ⚠️ MEASURE, WAIT, MEASURE AGAIN — AND ONLY THEN REPORT.
       The single 700ms check produced a false alarm on 2026-09-03: a guest hit
       a content refusal, went back to `ready` to retake, and the assertion
       caught the viewfinder mid-remount at 0x0. The session then finished
       perfectly. Nobody saw a black screen; we simply photographed a layout
       that had not settled yet and filed it as a failure.

       That matters more now than it did, for two reasons. The refusal copy we
       just shipped tells guests to take their photos again, so re-entry is no
       longer a rare path — it is the path we recommend. And failures are now on
       the dashboard, where a false alarm costs somebody real attention at a
       festival.

       A layout that is genuinely broken stays broken; one that is mid-remount
       recovers within a frame or two. So the second look is the whole test. */
    /* ⚠️ A HIDDEN BOOTH IS NOT A BROKEN BOOTH, AND THIS COST US THE ONLY TWO
       "failures" OF THE 19 SEP FESTIVAL DAY.
       RenFestSelfiePage keeps this whole subtree MOUNTED and sets `display:none`
       on it when the guest taps "Browse the Shoppe while you wait" - a view
       switch, never a route, because the poll has to keep running or the
       portrait is lost. A `display:none` element is still found by
       querySelector and measures exactly 0x0, which from in here is
       indistinguishable from a collapsed canvas.
       Both rows on 2026-09-19 were that: guests browsed the Shoppe while the
       painting finished, `step` went to `done` behind the Shoppe, and the
       assertion photographed a hidden box. Both sessions produced a portrait
       AND captured an email. Nobody saw a black screen, and the dashboard
       carried two failures that never happened.
       So ask whether the box is rendered at all before believing its zero. */
    const measure = () => {
      const el = document.querySelector('.rf-fill');
      const hidden = el
        ? (typeof el.checkVisibility === 'function' ? !el.checkVisibility() : el.offsetParent === null)
        : false;
      const r = el?.getBoundingClientRect();
      /* A MISSING box is still a real fault: `hidden` can only excuse a box
         that exists and has been deliberately taken out of the layout. */
      return { el, r, hidden, bad: !hidden && (!r || r.height < 120 || r.width < 120) };
    };

    const id = setTimeout(() => {
      /* Phone locked, or the guest switched apps mid-paint. Same class of false
         alarm as the Shoppe: the layout is not what is wrong. */
      if (document.hidden) return;
      const first = measure();
      if (!first.bad) return;

      setTimeout(() => {
        if (document.hidden) return;
        const again = measure();
        if (!again.bad) return;          // it settled, or it is simply not on screen.
        const { el, r } = again;
        probe('client_layout',
          `Media box collapsed on "${step}": ${Math.round(r?.width || 0)}x${Math.round(r?.height || 0)}`,
          {
            found: !!el,
            inner: `${window.innerWidth}x${window.innerHeight}`,
            visual: window.visualViewport ? Math.round(window.visualViewport.height) : null,
            dpr: window.devicePixelRatio,
            first_seen: `${Math.round(first.r?.width || 0)}x${Math.round(first.r?.height || 0)}`,
            persisted_ms: 900,
          });
      }, 900);
    }, 700);
    return () => clearTimeout(id);
  }, [step, probe]);

  /* ASSERTION 2 — the camera is actually producing frames.
     A live-looking <video> with zero videoWidth is the exact shape of the
     unmounted-node bug, and it is invisible until capture returns nothing. */
  /* ⚠️ IT WAITS FOR THE BROWSER, NOT FOR A CLOCK. The first version fired 2.5s
     after reaching `ready` and reported a dead camera whenever the guest had
     not yet tapped Allow — which is EVERY first-time guest, because nobody
     reads a permission dialog in two and a half seconds. Found 2026-08-27 on
     the first real test of the new build: one `client_camera_dead` row, no
     accompanying `client_camera` row, and `has_stream:false` simply meaning
     getUserMedia had not returned yet.

     A festival of first-time guests would have written that row thousands of
     times on opening morning and buried every genuine camera failure
     underneath it. `D-47` says an alarm nobody hears is not an alarm; an alarm
     that fires on the normal case is the same thing wearing a different hat.

     So: never report while the prompt is up, and re-check rather than fire on
     the first look. */
  useEffect(() => {
    if (step !== 'ready') return undefined;
    let cancelled = false;
    let waited = 0;
    const STEP_MS = 1500;
    const GIVE_UP_MS = 20000;   // long enough to find "Allow" on a strange phone

    const look = () => {
      if (cancelled) return;
      const v = videoRef.current;
      const track = streamRef.current?.getVideoTracks?.()[0];

      // Working. Nothing to say.
      if (v?.videoWidth && track?.readyState === 'live') return;

      /* ⚠️ THE DEADLINE IS ABSOLUTE. It used to read
         `if (camPendingRef.current || waited < GIVE_UP_MS)`, which meant that
         if getUserMedia never settled — the exact shape of a camera held by
         another tab — `camPendingRef` stayed true forever and this loop ran
         forever WITHOUT EVER REPORTING. Fixing "the alarm cries wolf" by making
         the alarm mute is not a fix, and it cost a real black-camera failure
         that left no row at all (2026-08-27, second occurrence, nothing in the
         table to look at).

         Wait, yes. Wait forever, no. `prompt_still_open` in the payload is what
         separates "they never tapped Allow" from "the camera never came back",
         which is the distinction the earlier version was reaching for. */
      if (waited < GIVE_UP_MS) {
        waited += STEP_MS;
        setTimeout(look, STEP_MS);
        return;
      }

      // Say it on screen too. A row in a table nobody is reading at 11am does
      // not help the person holding the phone.
      setCamErr(camPendingRef.current
        ? 'Waiting for camera permission. Tap "Allow" if your phone is asking.'
        : 'The camera is not showing. Close any other tabs or apps using it, then tap Retry.');
      setCamBlocked(true);

      probe('client_camera_dead',
        `Camera not producing frames after ${Math.round(waited / 1000)}s: `
        + `${v?.videoWidth || 0}x${v?.videoHeight || 0}, track=${track?.readyState || 'none'}`,
        {
          has_stream: !!streamRef.current,
          ready_state: v?.readyState ?? null,
          paused: v?.paused ?? null,
          waited_ms: waited,
          // ⚠️ Distinguishes "they never answered the prompt" from "the camera
          // is genuinely broken". Without it both look identical in the table.
          prompt_still_open: camPendingRef.current,
        });
    };

    const id = setTimeout(look, STEP_MS);
    return () => { cancelled = true; clearTimeout(id); };
  }, [step, probe]);

  /* ASSERTION 3 — anything the app throws anywhere, including in code paths
     we never wrote a try/catch around. */
  useEffect(() => {
    const onErr = (e) => probe('client_js', `${e.message} @ ${e.filename}:${e.lineno}:${e.colno}`);
    const onRej = (e) => probe('client_promise', String(e.reason?.message || e.reason || 'unknown rejection'));
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onRej);
    return () => {
      window.removeEventListener('error', onErr);
      window.removeEventListener('unhandledrejection', onRej);
      /* Speech outlives React. Without this the booth carries on counting
         down out loud to an empty tent after the guest has walked off. */
      hush();
    };
  }, [probe]);

  /* BREADCRUMBS — how far did they get before it went wrong? Attached to every
     report, so an abandoned run tells us WHICH screen lost them. */
  const trail = useRef([]);
  useEffect(() => {
    trail.current = [...trail.current, `${step}@${Math.round(performance.now() / 100) / 10}s`].slice(-14);
    /* Counted on ARRIVAL at `ready`, which is the only step a guest returns to.
       See the note on attemptRef. */
    if (step === 'ready') attemptRef.current += 1;
  }, [step]);

  /**
   * Ask the guest whether to retake a shot we think is blurry.
   *
   * ⚠️ IT ASKS, IT DOES NOT DECIDE. The metric is a heuristic on a 300x400
   * downsample; the guest is looking at the actual photograph. They are better
   * informed than we are, so they get the vote - and their answer is recorded,
   * which is how the threshold eventually stops being a guess.
   */
  function askRetake(label, url, score) {
    setRetake({ label, url, score });
    buzz([40, 80, 40]);
    say(`${label} looks blurry. Retake?`);
    return new Promise((resolve) => { retakeRef.current = resolve; });
  }

  function answerRetake(yes) {
    buzz(yes ? 30 : 12);
    hush();
    setRetake(null);
    const go = retakeRef.current;
    retakeRef.current = null;
    if (go) go(yes);
  }

  function waitForReady(n) {
    setWaitingFor(n);
    return new Promise((resolve) => { readyRef.current = resolve; });
  }

  function iAmReady() {
    buzz(14);
    unlockCues();
    setWaitingFor(0);
    const go = readyRef.current;
    readyRef.current = null;
    if (go) go();
  }

  /**
   * ⚠️ THE COUNTDOWN IS NOW SPOKEN AND FELT, NOT ONLY SHOWN.
   *
   * A guest with their head turned, a guest holding a prop up over their face,
   * a child looking at a parent instead of the phone — none of them are reading
   * the screen at the one moment that decides the photograph. So each tick goes
   * out on three channels at once and we accept that any of them may be missing:
   *
   *   sight ...... the numeral, always present, the only guaranteed one
   *   sound ...... spoken digits; needs volume up and, on iPhone, the ringer
   *                switch off. Silently absent otherwise.
   *   touch ...... vibration; Android only. iOS has never shipped the
   *                Vibration API and cannot be made to buzz from script at all.
   *
   * ⚠️ Speech is never awaited. A synthesiser that is blocked, silenced or
   * mid-bug must not be able to hold up the shutter — the picture is the
   * product, the voice is a courtesy.
   *
   * The last tick is deliberately louder in every channel: a double vibration
   * and the word held slightly slower. "One" is when people actually smile.
   */
  /**
   * ⚠️ SPEAK, THEN WAIT FOR THE VOICE TO ACTUALLY FINISH.
   *
   * The sequence used to run on fixed sleeps, which meant the countdown began
   * on a timer while the instruction was still being spoken - "turn your head
   * a little, but keep one eye on the glass" takes about three and a half
   * seconds and had two and a half, so it was cut off mid-sentence every time.
   * Guessing a duration for synthesised speech is not possible: it depends on
   * the voice, the rate, and the phone.
   *
   * So we wait for the utterance to END. `minMs` still applies as a floor, so
   * a phone with no voice at all (muted, blocked, unsupported - sayWatched
   * resolves immediately in all three) keeps the reading time it had before
   * and nothing gets faster than a person can read.
   */
  async function speakThenPause(text, minMs, timeoutMs) {
    const t0 = Date.now();
    await sayWatched(text, timeoutMs);
    const left = minMs - (Date.now() - t0);
    if (left > 0) await sleep(left);
  }

  /**
   * ⚠️ THE CUE IS SPOKEN BEFORE THE COUNTDOWN, NOT DURING IT.
   * Showing SMILE works; saying it works better, and saying it while also
   * saying "three" does not work at all - one utterance cancels the other.
   * So: the word, then the digits.
   */
  async function shutter(cueText, cueSpoken) {
    setCue(cueText);
    await speakThenPause(cueSpoken || cueText, 350, 2500);
    if (cancelRef.current) return null;
    for (let c = 3; c > 0; c -= 1) {
      if (cancelRef.current) return null;
      setCd(String(c));
      /* ⚠️ These are long on purpose. The old values were 6-16ms, which a
         linear resonant actuator cannot get moving in time for anyone to feel
         - see VIBE_FLOOR in boothCues.jsx. Tuned by the duration ladder in the
         Ledger, not by taste. */
      if (c === 1) { buzz([45, 70, 45]); say('one', { rate: 0.95 }); }
      else { buzz(40); say(String(c), { rate: 1.1 }); }
      await sleep(PACE.tick);
    }
    setCd('\u{1F4F8}'); buzz(120);
    await sleep(150);
    /* ⚠️ THE SCORE COMES FROM THE SHOT, NOT FROM `sharp`.
       This used to be `sharpScores.current.push(sharp)`, reading the React
       state the 300ms preview interval writes. `shutter` is an async closure
       created at one render, so it kept THAT render's value for its whole run -
       and every shot recorded the same stale number. The first two real phone
       runs logged [916,916,916] and [260,260,260]: three identical scores for
       three different photographs. The interval is also switched off during a
       `wide` pose, so for those frames it was stale by design as well.
       Measure the thing you are asking about, at the moment you have it. */
    const shot = grab();
    sharpScores.current.push(shot?.sharpness ?? null);
    setCd(''); setCue('');
    return shot;
  }

  function abandon() {
    hush();
    setStep('ready'); setCd(''); setPose(''); setCue(''); setWide(false); setWaitingFor(0);
    setRetake(null);
    readyRef.current = null;
    retakeRef.current = null;
  }

  async function beginCapture() {
    cancelRef.current = false;
    const eta = etaFor();
    buzz([12, 40, 12]);
    /* ⚠️ MUST HAPPEN INSIDE THIS TAP. iOS only lets speechSynthesis start from
       a user gesture; miss this and every spoken cue for the rest of the
       session is dropped without an error anywhere. Cheap, idempotent, and
       also called on the welcome tap so a slow first frame cannot lose it. */
    unlockCues();
    setStep('capturing');
    setResult(null); setRating(0); setFrames([]); setSaving('');
    setPartial({ url: null, index: null, count: 0 });
    partialIndexRef.current = null;
    setPrize(null); setPrizeShown(false);
    setPaintSec(0); setPatterIdx(0); setBgIdx(0); setBaseEta(eta); setEtaS(eta);

    /* ⚠️ RESET, or a retry appends to the previous attempt's scores and the
       array stops lining up with the frames it is supposed to describe. */
    sharpScores.current = [];
    blurPrompts.current = [];

    /**
     * Take one shot, and offer a single retake if it scores badly.
     *
     * `label` is what the guest is told: "Shot 2" on their own, "Person 3" in a
     * group - because in a group the useful information is WHO, not which
     * frame. The rejected frame is shown behind the question so the group can
     * see for themselves who moved.
     */
    const takeOne = async (cue, cueSay, label) => {
      let shot = await shutter(cue, cueSay);
      if (cancelRef.current) return shot;

      for (let r = 0; r < MAX_RETAKES_PER_SHOT; r += 1) {
        const score = shot?.sharpness;
        if (!shot?.dataUrl || score == null || score >= CAPTURE_BLUR_MIN) break;

        const again = await askRetake(label, shot.dataUrl, score);
        blurPrompts.current.push({ label, score, retaken: again });
        if (cancelRef.current || !again) break;

        // The rejected frame's score goes with it, or the array stops
        // describing the frames that were actually kept.
        sharpScores.current.pop();
        const second = await shutter(cue, cueSay);
        if (second?.dataUrl) shot = second;
        else break;
      }
      return shot;
    };

    const shots = [];

    if (isGroup) {
      /* ⚠️ SELF-PACED, ONE PERSON AT A TIME. Do not turn this into a rolling
         countdown: in a group nobody is ready at the same moment, and each
         person tapping their own button is the per-person affirmative action
         that carries the consent line. It is also the best bit to watch. */
      for (let i = 0; i < people; i += 1) {
        if (cancelRef.current) { abandon(); return; }
        /* ⚠️ THE INSTRUCTION HAS TO NAME THE TAP, IN BOTH CHANNELS.
           Observed with real groups, 2026-09-04: "people don't really know what
           to do. They're like, why isn't this going forward? They don't know
           they have to click." The old copy told them where to STAND and never
           mentioned the phone, so a group would arrange itself perfectly and
           then wait for a booth that was waiting for them. Whoever is holding
           the phone is usually not the person stepping up, which is exactly why
           it has to be said out loud as well as shown. */
        setPose(`Person ${i + 1}, step up — then tap below`);
        setWide(false);
        say(`Person ${i + 1}, step up. Then tap the button.`);
        await waitForReady(i + 1);
        if (cancelRef.current) { abandon(); return; }
        const shot = await takeOne(GROUP_CUE, 'Smile!', `Person ${i + 1}`);
        if (cancelRef.current) { abandon(); return; }
        if (shot?.dataUrl) { shots.push(shot.dataUrl); setFrames([...shots]); }
        if (i < people - 1) await sleep(PACE.after);
      }
    } else {
      for (let i = 0; i < N_FRAMES; i += 1) {
        if (cancelRef.current) { abandon(); return; }
        setPose(POSES[i].n);
        setWide(POSES[i].wide);
        setCue(''); setCd('');
        await speakThenPause(POSES[i].say, PACE.read, 5000);
        if (cancelRef.current) { abandon(); return; }
        const shot = await takeOne(POSES[i].cue, POSES[i].cueSay, `Shot ${i + 1}`);
        if (cancelRef.current) { abandon(); return; }
        if (shot?.dataUrl) { shots.push(shot.dataUrl); setFrames([...shots]); }
        if (i < N_FRAMES - 1) await sleep(PACE.after);
      }
    }

    setPose('');
    hush();

    /* ⚠️ THIS PATH USED TO FAIL SILENTLY. A black preview produced zero usable
       frames, showed the guest an error, and posted NOTHING to the server - so
       the single worst bug in the build was invisible in the Ledger. Every
       terminal client failure gets recorded, or we are debugging a festival by
       standing next to people. */
    if (!shots.length) {
      const msg = 'Nothing captured - the camera returned no frames';
      setCamErr('The camera did not send a picture. Tap ready to try again.');
      setStep('ready');
      await reportFailure(newJobId(), 'client_capture', msg, {
        video_w: videoRef.current?.videoWidth ?? null,
        video_h: videoRef.current?.videoHeight ?? null,
        has_stream: !!streamRef.current,
        track_state: streamRef.current?.getVideoTracks?.()[0]?.readyState ?? null,
      });
      return;
    }
    await generate(shots);
  }

  async function generate(rawShots) {
    const jobId = newJobId();
    setStep('painting');
    setPaintErr('');

    const clientMeta = {
      /* ⚠️ `D-15`, and the generator has ALWAYS written this — it reads
         `client_meta.device_id` at the insert and there is an index waiting on
         the column. The client simply never sent it, so `booth_run.device_id`
         was NULL on every row from launch until 2026-08-31 (`P11`). One line
         of the five that were missing. */
      device_id: getDeviceId(),
      ua: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      captured: rawShots.length,
      raw_bytes: b64Bytes(rawShots),
      sharpness: sharpScores.current.slice(),
      blur_prompts: blurPrompts.current.slice(),
      /* ⚠️ WHAT THE GUEST ACTUALLY GOT, NOT WHAT WE INTENDED. Support flags
         plus asked/started/accepted counts for speech and vibration. This is
         the only way we will ever know whether festival phones are hearing the
         countdown, because we cannot read the ringer switch and nobody is
         going to tell us. If say_asked is high and say_started is zero across
         a batch of iPhones, the audio cue is decorative and the choreography
         is carrying the whole sequence on its own. */
      cues: cueReport(),
    };


    try {
      setStatus('Preparing your photographs...');
      const { frames: shots, note } = await fitToBudget(rawShots);
      clientMeta.compression = note;

      // ⚠️ Frames go straight to Storage; the generator only sees paths.
      // Background functions are invoked asynchronously and AWS caps async
      // Lambda payloads at 256 KB - posting the images is impossible by
      // construction, no matter how hard they are compressed.
      /* ⚠️ THE GUEST IS NOT AN ENGINEER. This said `Sending 611 KB...`, which
         tells a person at a fairground nothing they can act on and quietly
         reads as "this is going to use your data". The size is still MEASURED
         and still recorded — `clientMeta.compression` carries it to the run
         row, where it is diagnostics rather than decoration. Joel, 2026-08-27:
         "weird, we don't want to broadcast that". */
      setStatus('Sending your photographs...');
      const { bucket, uploads } = await api.renfestSelfie.signUploads(jobId, shots.length);

      const framePaths = [];
      for (let i = 0; i < shots.length; i += 1) {
        const blob = await dataUrlToBlob(shots[i]);
        /* ⚠️ Plain fetch PUT, not supabase-js. The signed URL carries its own
           token, so the client needs no key and no project URL — which keeps
           @supabase/supabase-js out of the guest bundle entirely. See
           lib/api.js uploadSignedFrame. */
        try {
          await api.renfestSelfie.uploadFrame({ ...uploads[i], bucket, blob });
        } catch (e) {
          throw new Error(`Upload ${i + 1}/${shots.length} failed: ${e.message}`);
        }
        framePaths.push(uploads[i].path);
      }
      clientMeta.frame_paths = framePaths;
      setStatus('');

      /* ⚠️ TEST MODE FORKS HERE AND NOWHERE EARLIER. Everything above this line
         - camera, oval, countdown, cue, blur score, retake, compression, upload
         - is the guest's path, unmodified, because that is the only thing worth
           comparing painters on (`D-45`). The fork is one upload, several
         painters, one screen. */
      if (testRef.current) {
        await bakeOff(framePaths, clientMeta);
        return;
      }

      await api.renfestSelfie.generate({
        job_id: jobId, scene, frame_paths: framePaths, plates: [],
        likeness, banner: settings.banner,
        extra: [settings.extra, extra].filter(Boolean).join('. '),
        modelId: settings.modelId,
        people: isGroup ? people : 1,
        consent_at: consentAtRef.current,
        consent_version: CONSENT_VERSION,
        source: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'web',
        /* Which printed sign started this visit (sql/032). Null is the normal
           answer for anyone who typed the URL or scanned an unslugged code. */
        placement: getPlacement(),
        client_meta: clientMeta,
      });

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      for (;;) {
        await sleep(POLL_MS);
        if (Date.now() > deadline) throw new Error('Timed out. Ask a member of staff.');
        const r = await api.renfestSelfie.status(jobId);

        // ⚠️ Swap on partial_index, NOT the URL - every poll signs a fresh URL,
        // so keying off it would re-download and flicker twice a second.
        /* ⚠️ HELD BACK ON PURPOSE. The prize is decided before the first
           brushstroke, so it is available on the very first poll — and firing
           it instantly would put a flashing "YOU WON" on screen while the guest
           is still looking for their own face. It lands once real painting has
           started, which is when there is something to celebrate ALONGSIDE.
           Joel: the winning should be "things that are happening while they are
           watching their photo be made". */
        if (r.prize && !prize) setPrize(r.prize);

        if (r.partial_url && r.partial_index !== partialIndexRef.current) {
          partialIndexRef.current = r.partial_index;
          setPartial({ url: r.partial_url, index: r.partial_index, count: r.partial_count || 1 });
          buzz(6);
          if (!prizeShown) { setPrizeShown(true); buzz([12, 40, 12]); }
        }
        if (r.status === 'done') {
          buzz([20, 60, 20, 60, 40]);
          setResult(r); setStep('done');
          return;
        }
        if (r.status === 'error') {
          /* ⚠️ THE CLASSIFIED MESSAGE WINS OVER `r.error`. `r.error` is the
             provider's own words, kept for the console, and it is the wrong
             thing to put in front of a guest. Only fall back to it when the
             failure was never classified. */
          throw new Error(
            ERROR_MESSAGES[r.error_kind]
            || r.error
            || 'The painter dropped his brush. Try again.',
          );
        }

        /* ⚠️ `blocked` IS TERMINAL AND THE GUEST MUST BE TOLD.
           The generator runs as a Netlify BACKGROUND function, so its 429 is
           returned to nobody - the gateway acked 202 before it ran. The row is
           the only channel. Without this branch a capped or paused booth leaves
           the guest watching a spinner until the poll deadline, which reads as
           "broken" rather than "not right now". Found 2026-08-22 by tripping
           the hourly cap against production. */
        if (r.status === 'blocked') {
          throw new Error(BLOCK_MESSAGES[r.block_reason] || 'The booth is taking a break. Try again shortly.');
        }
      }
    } catch (e) {
      const msg = String(e.message || e);
      setStatus(''); setCamErr(msg); setPaintErr(msg); setStep('ready');
      await reportFailure(jobId, msg.toLowerCase().includes('upload') ? 'client_upload' : 'client_post', msg, clientMeta);
    }
  }

  /**
   * Run the bake-off: every painter x every frame subset, on ONE capture.
   *
   * ⚠️ IN WAVES, NOT ALL AT ONCE. A 4x4 matrix is sixteen image generations. Fired
   * together they would be sixteen concurrent background functions against one
   * OpenAI account whose rate limit nobody has measured (`Q-12`) - and a rate-limit
   * cascade does not fail politely, it fails as a grid of error cards after the money
   * is already committed. Four at a time costs a couple of extra minutes of standing
   * still and makes the data trustworthy.
   *
   * ⚠️ THE FRAMES ARE SLICED FROM ONE UPLOAD, never re-shot. Variant "1+3" and variant
   * "1+2+3" are looking at the same photographs; only the selection differs. That is
   * the entire experiment (`D-46`).
   */
  async function bakeOff(framePaths, clientMeta) {
    const cfg = testRef.current;
    const variants = cfg.variants?.length ? cfg.variants : [{ label: '1+2+3', idx: [0, 1, 2] }];

    const runs = [];
    for (const modelId of cfg.painters) {
      for (const v of variants) {
        const paths = v.idx.map((i) => framePaths[i]).filter(Boolean);
        /* A variant asking for a frame the capture never produced - a group run,
           or a retake that came back empty - would otherwise silently become a
           different variant than the one on the label. */
        if (!paths.length) continue;
        runs.push({
          job_id: `${cfg.batch}_${runs.length}`,
          model_id: modelId,
          variant: v.label,
          frames: paths.length,
          paths,
        });
      }
    }
    setTestRuns(runs.map((r) => ({ ...r, row: null })));

    const done = new Map();
    const paint = (list) => setTestRuns(runs.map((r) => ({ ...r, row: list.get(r.job_id) || null })));
    const rows = new Map();

    const WAVE = 4;
    for (let w = 0; w < runs.length; w += WAVE) {
      if (cancelRef.current) return;
      const wave = runs.slice(w, w + WAVE);
      setStatus(runs.length > WAVE
        ? `Painting ${w + 1}-${Math.min(w + WAVE, runs.length)} of ${runs.length}...`
        : `Painting ${runs.length} versions...`);

      await Promise.all(wave.map((r) => api.renfestSelfie.generate({
        job_id: r.job_id,
        scene,
        frame_paths: r.paths,
        plates: [],
        likeness,
        banner: settings.banner,
        extra: [settings.extra, extra].filter(Boolean).join('. '),
        modelId: r.model_id,
        /* ⚠️ null, not undefined. `undefined` means "use this model's configured
           fallback", which would quietly paint one cell of the matrix with a
           painter nobody put in it. In a bake-off a failure IS the result. */
        fallbackId: null,
        people: isGroup ? people : 1,
        consent_at: consentAtRef.current,
        consent_version: CONSENT_VERSION,
        source: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'web',
        test_batch: cfg.batch,
        test_variant: r.variant,
        client_meta: clientMeta,
      })));

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      for (;;) {
        if (cancelRef.current) return;
        await sleep(POLL_MS);
        await Promise.all(wave.map(async (r) => {
          if (done.has(r.job_id)) return;
          try {
            const row = await api.renfestSelfie.status(r.job_id);
            rows.set(r.job_id, row);
            if (['done', 'error', 'blocked'].includes(row.status)) done.set(r.job_id, true);
          } catch (e) {
            /* One poll failing must not abandon the wave - those runs are already
               painting and already costing money. */
            rows.set(r.job_id, { status: 'running', error: String(e.message || e) });
          }
        }));
        paint(rows);
        if (wave.every((r) => done.has(r.job_id))) break;
        if (Date.now() > deadline) break;
      }
    }

    setStatus('');
    buzz([20, 60, 20, 60, 40]);
    setStep('compare');
  }

  /** Record which cell won, from the booth, with the booth's own token. */
  async function pickWinner(jobId) {
    buzz(14);
    setWinner(jobId);
    setRevealed(true);
    try {
      await api.renfestSelfie.score({
        action: 'pick_test_winner', job_id: jobId, test_batch: testRef.current?.batch,
      });
    } catch (e) {
      /* The verdict is worth having recorded, and it is not worth losing the
         screen over. Say so rather than swallowing it. */
      setCamErr(`Picked - but not recorded: ${e.message}`);
    }
  }

  async function score(n) {
    if (!result) return;
    buzz(10); setRating(n);
    try { await api.renfestSelfie.score({ job_id: result.job_id, score_likeness: n }); }
    catch (e) { console.error('[renfest-selfie] score failed', e); }
  }

  /**
   * ⚠️ THIS THROWS ON PURPOSE. EmailCapture shows the error next to the address
   * and keeps the guest on the confirm step so they can fix a typo and try
   * again. Swallowing it here would show "Sent!" for a message that never left.
   */
  async function sendPhoto(email, optIn) {
    try {
      const res = await api.renfestSelfie.sendPhoto(result.job_id, email, optIn);
      if (res && res.ok === false) throw new Error(res.error || 'That would not send.');
      return res;
    } catch (e) {
      /* `e.message` is written for a log - endpoint, status, raw body.
         `e.serverError` is the sentence the function wrote for the person. */
      throw new Error(e.serverError || 'That would not send. Check the address and try again.');
    }
  }

  async function doSave(mode) {
    buzz(14); setSaving('working');
    const filename = portraitFilename(result);
    try {
      /* ⚠️ THE FRAMED ONE WHEN THERE IS ONE. A share is the guest showing
         somebody else, which is exactly the moment the branding and booth #383
         are worth carrying. Falls back to the clean portrait only when the
         composite has not landed. */
      const src = result.framed_url || result.image_url;
      if (mode === 'share') await sharePortrait(src, filename);
      else await downloadPortrait(src, filename);
      setSaving('Saved');
    } catch (e) {
      if (e.name === 'AbortError') { setSaving(''); return; }
      setSaving(''); setCamErr(`Could not save: ${e.message}`);
    }
    setTimeout(() => setSaving(''), 2500);
  }

  function startOver() {
    setLikeness('full'); setExtra(''); setShowDetail(false);
    cancelRef.current = true;
    buzz(10);
    setResult(null); setRating(0); setFrames([]); setSaving(''); setCamErr(''); setPaintErr('');
    /* The test session survives - the whole point is running another batch
       without going back to the console. The last batch's results do not. */
    setTestRuns([]); setRevealed(false); setWinner(null);
    setCd(''); setPose(''); setCue(''); setWaitingFor(0); readyRef.current = null;
    setPartial({ url: null, index: null, count: 0 });
    partialIndexRef.current = null;
    setEmailSent(false);
    setScene(null); setIsGroup(false); setPeople(2);
    setStep('who');
  }

  /* ─── derived ─────────────────────────────────────────────────── */

  const shotCount = isGroup ? people : N_FRAMES;
  const estCost = MODEL_OUT + FRAME_IN_COST * shotCount;
  const remaining = Math.max(1, etaS - paintSec);
  const overtime = paintSec > baseEta;
  const partialPct = partial.count ? partial.count / (PARTIAL_TARGET + 1) : 0;
  const ringPct = Math.min(0.97, Math.max(partialPct, Math.min(0.9, paintSec / Math.max(1, baseEta))));
  const patter = overtime
    ? OVERTIME_PATTER[patterIdx % OVERTIME_PATTER.length]
    : PATTER[patterIdx % PATTER.length];

  const DOTS = (isGroup ? 5 : 4) - (FORCED_STYLE ? 1 : 0);
  /* More than one frame subset in play means the frame count is part of what is
     being compared, so it has to appear beside every result. */
  const manyVariants = new Set(testRuns.map((r) => r.variant)).size > 1;

  const R = 54, CIRC = 2 * Math.PI * R;

  /* ─── the global progress bar's ONE source of truth ─────────────
     ⚠️ THIS REPORTS `ringPct`. IT DOES NOT RECOMPUTE IT.
     The bar pinned to the page and the bar inside the picture read the same
     variable, one line above, so they cannot disagree. Joel, 2026-08-27: "It's
     okay to have it in two spots, but having conflicting numbers is not okay."
     If a second percentage is ever calculated for the global bar, that promise
     is broken and nobody will notice until two numbers are on one screen.

     ⚠️ THE POLL, THE COUNTDOWN AND THE PARTIALS STAY HERE. Only the derived
     numbers travel. Moving the state up would put the guest's whole run inside
     a component that the Shoppe re-renders, and this file's rule is that
     nothing about a running portrait may depend on which room the guest is
     standing in.

     Null means "nothing true to report", and the bar renders nothing. */
  useEffect(() => {
    if (!onProgress) return;
    if (step === 'painting') {
      onProgress({ phase: 'painting', pct: ringPct, passes: partial.count, target: PARTIAL_TARGET });
    } else if (step === 'done') {
      onProgress({ phase: 'done', pct: 1, passes: 0, target: PARTIAL_TARGET });
    } else if (paintErr) {
      onProgress({ phase: 'failed', pct: 0, passes: 0, target: PARTIAL_TARGET });
    } else {
      onProgress(null);
    }
  }, [step, ringPct, partial.count, paintErr, onProgress]);

  /* ─── swap in the framed composite ──────────────────────────────
     ⚠️ WHAT THE GUEST SEES MUST BE WHAT THE GUEST GETS. The finished screen
     showed the CLEAN portrait while the email delivered the FRAMED one, so the
     picture they fell in love with was not the picture that arrived. Joel,
     2026-08-27: "I still don't see the framed photo, so still bait and switch."

     ⚠️ IT CANNOT SIMPLY BE FETCHED AT `done`. The composite does not exist yet
     at that moment — it is built after the run is marked finished, on purpose.
     So the clean portrait goes up immediately (never make a guest wait to see
     their own face) and this quietly replaces it a few seconds later.

     ⚠️ FAILING IS SILENT AND HARMLESS. If the composite never appears the guest
     keeps the clean portrait and no error is shown, because there is nothing
     they could do about it and nothing has actually gone wrong for them. */
  useEffect(() => {
    if (step !== 'done' || !result?.job_id || result.framed_url) return undefined;
    let alive = true;
    let tries = 0;
    let id = null;
    /* The composite is ~8s away. Starting the interval immediately would fire
       four or five polls into a window where the answer cannot yet be yes. */
    const start = setTimeout(() => {
      if (!alive) return;
      id = setInterval(tick, FRAMED_POLL_MS);
    }, FRAMED_FIRST_DELAY_MS);

    async function tick() {
      if (!alive) return;
      tries += 1;
      try {
        const r = await api.renfestSelfie.status(result.job_id);
        if (alive && r?.framed_url) {
          setResult((prev) => (prev ? { ...prev, framed_url: r.framed_url } : prev));
          clearInterval(id);
          return;
        }
      } catch { /* the clean portrait is already on screen; say nothing */ }
      if (tries >= FRAMED_TRIES) {
        clearInterval(id);
        /* ⚠️ SILENT TO THE GUEST, LOUD IN THE LOG. They keep the clean portrait
           and there is nothing they could do, so nothing is shown. But this
           firing means either the compositor failed for this run or the column
           stopped arriving — and the FIRST version of this feature failed on
           every single run with no trace anywhere, which is how it shipped
           broken twice. An alarm that never sounds is not an alarm (`D-47`). */
        reportFailureRef.current?.(
          result.job_id, 'client_composite',
          `No framed_url after ${FRAMED_TRIES} tries `
          + `(${(FRAMED_FIRST_DELAY_MS + FRAMED_TRIES * FRAMED_POLL_MS) / 1000}s); `
          + 'production composite is ~8s, so this run did not compose',
        );
      }
    }
    return () => { alive = false; clearTimeout(start); if (id) clearInterval(id); };
  }, [step, result]);


  /* ─── viewfinder (shared by ready + capturing) ────────────────── */

  /* The cue takes the top band the moment it appears; before that the pose
     instruction sits there, so the guest reads it in the two places their eyes
     already are rather than in the one place we chose for them. */
  const topLine = cue || poseText;

  const viewfinder = (
    <div className="relative rounded-2xl overflow-hidden bg-black w-full h-full">
      <video
        ref={attachVideo} autoPlay playsInline muted
        className="w-full h-full block"
        style={{ transform: 'scaleX(-1)', objectFit: 'cover' }}
      />

      {wide ? (
        /* Corner brackets: "get all of you in", not "come closer". Deliberately
           static - an animation pulling outward tested as one more thing to
           look at during a three-second countdown. */
        <div className="absolute inset-0 pointer-events-none">
          {[
            { top: '5%', left: '4%', bt: 1, bl: 1 },
            { top: '5%', right: '4%', bt: 1, br: 1 },
            { bottom: '5%', left: '4%', bb: 1, bl: 1 },
            { bottom: '5%', right: '4%', bb: 1, br: 1 },
          ].map((c, i) => (
            <span key={i} style={{
              position: 'absolute', width: '18%', height: '13%',
              top: c.top, bottom: c.bottom, left: c.left, right: c.right,
              borderTop: c.bt ? '4px solid rgba(255,255,255,.9)' : 'none',
              borderBottom: c.bb ? '4px solid rgba(255,255,255,.9)' : 'none',
              borderLeft: c.bl ? '4px solid rgba(255,255,255,.9)' : 'none',
              borderRight: c.br ? '4px solid rgba(255,255,255,.9)' : 'none',
              borderRadius: 6,
              filter: 'drop-shadow(0 0 8px rgba(0,0,0,.8))',
            }} />
          ))}
        </div>
      ) : (
        <div className="absolute inset-0 pointer-events-none flex items-start justify-center">
          <div
            className={framedOk ? 'rf-halo' : ''}
            style={{
              /* ⚠️ SIZED FROM HEIGHT WITH A FIXED ASPECT, NOT FROM WIDTH.
                 It used to be 64% x 68% of the box. Once the viewfinder started
                 filling a tall container those percentages stretched it into a
                 long tube that no head fits. aspect-ratio pins it to a face
                 shape whatever the box does. */
              marginTop: '7%', height: '58%', aspectRatio: '0.76', borderRadius: '50%',
              border: framedOk ? '3px solid rgba(255,255,255,.95)' : '2px dashed rgba(255,255,255,.35)',
              boxShadow: framedOk ? '0 0 44px 10px rgba(255,255,255,.55), inset 0 0 30px rgba(255,255,255,.25)' : 'none',
              transition: 'box-shadow .3s, border-color .3s',
            }}
          />
        </div>
      )}

      {/* ⚠️ THE INSTRUCTION IS PRINTED TWICE, TOP AND BOTTOM, ON PURPOSE.
          A single band at the bottom lost every guest who was looking at the
          middle of the frame — which is everyone, because that is where their
          own face is. Duplication is normally a smell; on a three-second timer
          in bright sun it is the cheapest way to make an instruction unmissable
          wherever the eye happens to be.

          The top band carries the CUE once the countdown starts and the POSE
          before that, so the loud word and the instruction never collide in
          the same place. */}
      {topLine && (
        <div className="absolute top-0 left-0 right-0 pt-3 pb-8 text-center pointer-events-none"
             style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,.85), transparent)' }}>
          <span className={`rf-ink leading-none${cue ? ' rf-cue' : ''}`} style={{
            color: GOLD, display: 'block',
            fontSize: topLine.length > 22
              ? 'clamp(1.55rem, 6.6vw, 2.6rem)'
              : topLine.length > 8
                ? 'clamp(2.2rem, 11vw, 4.2rem)'
                : 'clamp(3.2rem, 17vw, 6.5rem)',
            textShadow: '0 4px 0 rgba(0,0,0,.75), 0 2px 0 rgba(0,0,0,.75), 0 0 44px rgba(240,180,41,.5)',
          }}>{topLine}</span>
        </div>
      )}

      {countdown && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {/* key={countdown} restarts the pop on every tick. The glyph is
              centred by a full-width block rather than by flex, because
              MedievalSharp's digits have uneven side bearings and flex centres
              the BOX, not the ink - which is what made the numeral look
              off-centre. */}
          <span key={countdown} className="rf-count">{countdown}</span>
        </div>
      )}

      {poseText && (
        /* ⚠️ IT NO LONGER DISAPPEARS WHEN THE COUNTDOWN STARTS. It used to be
           hidden the moment a cue or a numeral appeared, which meant the
           instruction vanished at exactly the second the guest was trying to
           follow it — they turned their head, looked back for confirmation,
           and the words were gone. It now holds all the way to the shutter. */
        <div className="absolute bottom-0 left-0 right-0 px-3 py-4 text-center"
             style={{ background: 'linear-gradient(to top, rgba(24,17,10,.97), rgba(24,17,10,.86))', color: '#f6ecd8' }}>
          <span className="rf-ink block" style={{
            color: GOLD, lineHeight: 1.05,
            fontSize: poseText.length > 22
              ? 'clamp(1.35rem, 5.8vw, 2.2rem)'
              : 'clamp(1.6rem, 7.5vw, 2.8rem)',
            textShadow: '0 3px 0 rgba(0,0,0,.75), 0 0 30px rgba(240,180,41,.35)',
          }}>{poseText}</span>
        </div>
      )}
    </div>
  );

  /* ─── render ──────────────────────────────────────────────────── */

  return (
    <div className={`rf-booth${dark ? '' : ' is-light'}`}>
      {/* ⚠️ TEST MODE MUST BE VISIBLE FROM THE FIRST SCREEN. An admin session
          paints several portraits per capture and spends several times the
          money, and it looks exactly like the guest booth otherwise. A bar that
          says so - and that can be left in one tap - is the difference between
          "I am testing" and "why did that cost 23 cents". */}
      {testRef.current && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '.5rem', padding: '.3rem .7rem', background: '#3b2f12',
          borderBottom: '1px solid rgba(247,228,187,.25)', flex: '0 0 auto',
        }}>
          <span className="rf-ink" style={{ fontSize: '.72rem', letterSpacing: '.04em' }}>
            TEST MODE · {testRef.current.painters.length} painters
          </span>
          <button type="button" className="rf-chip" style={{ minHeight: 28, fontSize: '.7rem' }}
                  onClick={() => { endTestSession(); window.location.reload(); }}>
            Leave
          </button>
        </div>
      )}
      <style>{`
        /* ⚠️ HEIGHT, NOT MIN-HEIGHT, AND EVERY BOX BETWEEN HERE AND THE
           VIEWFINDER MUST BE ABLE TO SHRINK.
           The previous version used min-height:100% and let the viewfinder size
           itself from aspect-ratio 3/4 - so on a 448px-wide phone it demanded
           597px of height regardless of what was available, and shoved the
           capture button off the bottom. The media must FILL the space that is
           left, never dictate it. A min-height of 0 on every flex ancestor is what
           makes that possible; without it flex items refuse to shrink below
           their content and the overflow comes straight back. */
        /* ⚠️ EVERY LINK IN THIS CHAIN NEEDS A DEFINITE HEIGHT.
           .rf-step used height:100% while .rf-booth had none, so it resolved
           against auto, sized to content, and the painting canvas - a flex:1
           box with no intrinsic content - collapsed to zero. The generation
           still ran, invisibly. Percentage heights are only as good as the
           weakest ancestor. */
        .rf-booth { height:100%; display:flex; flex-direction:column; }
        .rf-step { flex:1; min-height:0; display:flex; flex-direction:column; }
        .rf-step-head { display:flex; align-items:center; justify-content:space-between; min-height:44px; flex:none; }
        .rf-step-body { flex:1; min-height:0; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.7rem; padding:.35rem 0 .6rem; width:100%; }
        .rf-step-foot { padding-bottom:.4rem; flex:none; }
        .rf-fill { flex:1; min-height:180px; width:100%; display:flex; }
        .rf-fill > * { max-height:100%; }
        .rf-step-title { flex:none; color:${GOLD}; font-size:clamp(1.5rem,6vw,2.4rem); line-height:1.05; text-align:center; text-shadow:0 3px 0 rgba(0,0,0,.5); }
        .rf-step-sub { flex:none; color:rgba(247,228,187,.72); font-size:.95rem; text-align:center; margin-top:-.35rem; }
        .rf-back { min-width:48px; min-height:48px; display:flex; align-items:center; justify-content:center; color:rgba(247,228,187,.75); }
        .rf-dots { display:flex; gap:.4rem; }
        .rf-dot { width:9px; height:9px; border-radius:50%; background:rgba(247,228,187,.25); }
        .rf-dot.is-on { background:${GOLD}; box-shadow:0 0 10px rgba(240,180,41,.7); }
        .rf-pick { width:100%; display:flex; flex-direction:column; align-items:center; gap:.5rem; padding:1.15rem 1rem;
          border-radius:1.2rem; background:linear-gradient(180deg,#7b5631,#54371b); border:1px solid rgba(0,0,0,.55);
          box-shadow: inset 0 1px 0 rgba(255,226,176,.22), 0 3px 0 rgba(0,0,0,.35); color:#f7e4bb; min-height:104px;
          font-family:'MedievalSharp','Papyrus',fantasy; font-size:1.4rem; transition:transform .08s; }
        .rf-pick:active { transform:translateY(3px); }
        .rf-num { min-width:84px; min-height:84px; border-radius:1.1rem; font-size:2.2rem; }

        /* Extruded gold numeral. Gradient-filled text plus stacked drop-shadows
           reads as carved rather than printed, which is the fairground register
           we are after. */
        .rf-count {
          font-family:'MedievalSharp','Papyrus',fantasy;
          display:block; width:100%; text-align:center; line-height:1;
          font-size:clamp(7rem,36vw,15rem);
          background:linear-gradient(180deg,#fffbe8 0%,#f7cf74 42%,#d1901f 78%,#a86c12 100%);
          -webkit-background-clip:text; background-clip:text; color:transparent;
          filter:
            drop-shadow(0 3px 0 rgba(90,50,10,.9))
            drop-shadow(0 6px 0 rgba(50,28,6,.75))
            drop-shadow(0 14px 28px rgba(0,0,0,.85));
          animation: rf-pop .55s cubic-bezier(.2,1.4,.4,1) both;
        }
        @keyframes rf-pop {
          0%   { transform:scale(1.5);  opacity:0; }
          55%  { transform:scale(.97);  opacity:1; }
          100% { transform:scale(1);    opacity:1; }
        }
        /* ⚠️ ~0.9Hz on purpose. Anything above 3Hz is a seizure risk, and this
           has to run in front of thousands of strangers. Breathing, not
           flashing. */
        @keyframes rf-cue-pulse {
          0%,100% { transform:scale(1);    filter:brightness(1); }
          50%     { transform:scale(1.05); filter:brightness(1.22); }
        }
        .rf-cue { animation: rf-cue-pulse 1.1s ease-in-out infinite; transform-origin:center; }

        /* The volume bar fills, holds at full, and drops back. It is a picture
           of the thing we are asking them to do, in the shape every phone
           already uses, which is faster to understand than any sentence. */
        @keyframes rf-vol {
          0%   { width:14%; }
          45%  { width:100%; }
          80%  { width:100%; }
          100% { width:14%; }
        }
        .rf-volfill { width:14%; animation: rf-vol 2.6s ease-in-out infinite; }

        /* ⚠️ THE ARROW THAT TELLS A GROUP THE PHONE IS WAITING ON THEM.
           Same ~0.9Hz budget as rf-cue-pulse above: breathing, not flashing,
           because this runs in front of thousands of strangers and anything
           past 3Hz is a seizure risk. Travel is 6px — enough for peripheral
           vision to catch, small enough not to read as a broken layout. */
        @keyframes rf-nudge-bob {
          0%,100% { transform:translateY(0);   opacity:.75; }
          50%     { transform:translateY(6px); opacity:1;   }
        }
        .rf-nudge { animation: rf-nudge-bob 1.1s ease-in-out infinite; }

        /* ⚠️ AND IT ALL STOPS FOR ANYONE WHO ASKED IT TO. A guest with
           vestibular sensitivity set this preference on their own phone; a
           festival booth is exactly the loud, disorienting place where it
           matters most. The affordance survives without the motion: the arrow
           still points and the button still says "Tap". */
        @media (prefers-reduced-motion: reduce) {
          .rf-nudge, .rf-cue, .rf-volfill { animation: none !important; }
          .rf-nudge { opacity: 1; }
        }
      `}</style>

      {/* ══ WELCOME ═══════════════════════════════════════════════════
          ⚠️ The checkbox gates the button. That IS the enforcement, and it is
          never pre-ticked — a pre-ticked box turns a valid consent into an
          invalid one. Age and photo consent are one affirmative, not two:
          splitting them doubles the friction and gains nothing. */}
      {step === 'welcome' && (
        <Step
          footer={
            <>
              <label className="flex items-start gap-3 mb-3 cursor-pointer px-1">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => { buzz(8); setAgreed(e.target.checked); }}
                  style={{ width: 26, height: 26, marginTop: 2, accentColor: GOLD, flex: 'none' }}
                />
                <span className="rf-ink" style={{ color: '#f7e4bb', fontSize: '.98rem', lineHeight: 1.3 }}>
                  {CONSENT_TEXT}
                </span>
              </label>
              <button
                type="button"
                disabled={!agreed}
                onClick={() => {
                  buzz(16);
                  /* First real tap of the session — the only reliable moment to
                     unlock speech on iOS. See boothCues.jsx. */
                  unlockCues();
                  consentAtRef.current = new Date().toISOString();
                  setStep('volume');
                }}
                className="rf-big w-full rounded-2xl px-4 py-5"
                style={{ fontSize: '1.6rem', opacity: agreed ? 1 : 0.4 }}
              >
                Start
              </button>
              <p className="rf-ink text-center mt-2" style={{ color: 'rgba(247,228,187,.5)', fontSize: '.76rem', lineHeight: 1.35 }}>
                {CONSENT_SUBTEXT}
              </p>
            </>
          }
        >
          <h1 className="rf-ink" style={{
            color: GOLD, fontSize: 'clamp(2.4rem,12vw,4.2rem)', lineHeight: 1, textAlign: 'center',
            textShadow: '0 4px 0 rgba(0,0,0,.55), 0 0 40px rgba(240,180,41,.3)',
          }}>
            {THEME?.welcome?.title || 'Free Portrait'}
          </h1>
          <p className="rf-ink" style={{ color: '#f7e4bb', fontSize: '1.3rem', textAlign: 'center' }}>
            {THEME?.welcome?.sub || 'Be somewhere you\u2019re not'}
          </p>
          <p className="rf-ink" style={{ color: 'rgba(247,228,187,.65)', textAlign: 'center' }}>
            {THEME?.welcome?.line || 'KC Renaissance Festival \u00b7 2026'}
          </p>
        </Step>
      )}

      {/* ══ VOLUME ════════════════════════════════════════════════════
          ⚠️ EVERY GUEST, EVERY TIME. This runs on the guest's own phone off a
          QR code, so there is no "returning device" and nothing to remember —
          for every single person who scans, this is the first time. Anything
          cached here would be written once and read never.

          ⚠️ IT IS A PICTURE OF A VOLUME BAR, NOT A QUESTION. We cannot read
          system volume or the ringer switch from a web page and we cannot
          change them, so there is nothing to ask and nothing to fix. The one
          useful thing we can do is show the control they need to touch, in the
          shape they already recognise, and talk at the same moment so they can
          hear whether it worked. One button out. */}
      {step === 'volume' && (
        <Step
          title="Turn your volume up"
          /* ⚠️ PLAIN SPOKEN ENGLISH. This read "I count you in out loud",
             which is not a sentence anyone says. Joel, 2026-08-27, and he was
             right. Hidden when the synthesiser refused to start — a screen
             promising a voice that will never speak is worse than silence. */
          sub={speechDead ? null : "We'll count down from 3"}
          onBack={() => { hush(); setStep('welcome'); }}
          footer={
            <button
              type="button"
              className="rf-big w-full rounded-2xl px-4 py-5"
              style={{ fontSize: '1.6rem' }}
              onClick={() => { buzz(35); hush(); setStep('who'); }}
            >
              Got it
            </button>
          }
        >
          <div className="w-full flex items-center gap-3" style={{ maxWidth: 460 }}>
            <span style={{ fontSize: 'clamp(2.2rem,9vw,3.2rem)', lineHeight: 1 }} aria-hidden="true">
              {'\u{1F50A}'}
            </span>
            <div style={{
              flex: 1, height: 30, borderRadius: 15, overflow: 'hidden',
              background: 'rgba(0,0,0,.5)', border: '1px solid rgba(0,0,0,.65)',
              boxShadow: 'inset 0 2px 6px rgba(0,0,0,.5)',
            }}>
              <div className="rf-volfill" style={{
                height: '100%', borderRadius: 15,
                background: `linear-gradient(90deg,#a86c12,${GOLD})`,
                boxShadow: '0 0 18px rgba(240,180,41,.55)',
              }} />
            </div>
          </div>

          <p className="rf-ink" style={{
            color: '#f7e4bb', textAlign: 'center', maxWidth: 460,
            fontSize: 'clamp(1.15rem,5vw,1.5rem)', lineHeight: 1.3,
          }}>
            {speechDead
              ? "This phone's voice isn't available - the pictures will guide you instead."
              : 'Press the volume buttons on the side of your phone'}
          </p>
        </Step>
      )}

      {/* ══ WHO ══ auto-advance: tapping the choice IS the next button ══ */}
      {step === 'who' && (
        <Step
          title="Who's in the picture?"
          dots={DOTS} dotIndex={0}
          onBack={() => setStep('welcome')}
        >
          <button type="button" className="rf-pick" onClick={() => { buzz(10); setIsGroup(false); setStep('scene'); }}>
            <User size={44} style={{ color: GOLD }} />
            Just me
          </button>
          <button type="button" className="rf-pick" onClick={() => { buzz(10); setIsGroup(true); setStep('count'); }}>
            <Users size={44} style={{ color: GOLD }} />
            A group of us
          </button>
        </Step>
      )}

      {step === 'count' && (
        <Step
          title="How many of you?"
          sub="Everyone gets their own photo, one at a time"
          dots={DOTS} dotIndex={1}
          onBack={() => setStep('who')}
        >
          <div className="flex flex-wrap justify-center gap-3">
            {Array.from({ length: MAX_PEOPLE - 1 }, (_, i) => i + 2).map((n) => (
              <button key={n} type="button" className="rf-pick rf-num"
                      onClick={() => { buzz(10); setPeople(n); setStep('scene'); }}>
                {n}
              </button>
            ))}
          </div>
        </Step>
      )}

      {step === 'scene' && (
        <Step
          title="Where shall we paint you?"
          dots={DOTS} dotIndex={isGroup ? 2 : 1}
          onBack={() => setStep(isGroup ? 'count' : 'who')}
        >
          <div className="grid grid-cols-2 gap-3 w-full overflow-y-auto rf-scroll">
            {SCENES.map((s) => (
              <button key={s.k} type="button" className="rf-pick" style={{ minHeight: 88, fontSize: '1rem' }}
                      onClick={() => { buzz(10); setScene(s.k); setStep(FORCED_STYLE ? 'ready' : 'style'); }}>
                <span style={{ fontSize: '2.4rem', lineHeight: 1 }}>{s.e}</span>
                {s.n}
              </button>
            ))}
          </div>
        </Step>
      )}

      {step === 'style' && (
        <Step
          title="How shall we paint you?"
          dots={DOTS} dotIndex={isGroup ? 3 : 2}
          onBack={() => setStep('scene')}
          footer={
            showDetail ? (
              <>
                <input
                  className="rf-input"
                  value={extra}
                  onChange={(e) => setExtra(e.target.value)}
                  placeholder={THEME?.detailPlaceholder || 'e.g. wearing a green velvet doublet'}
                  autoFocus
                />
                <button type="button" onClick={() => { buzz(10); setStep('ready'); }}
                        className="rf-big w-full rounded-2xl px-4 py-4 mt-2" style={{ fontSize: '1.3rem' }}>
                  Done
                </button>
              </>
            ) : (
              /* ⚠️ OPTIONAL, HIDDEN, AND ONLY ON THIS SCREEN. Free text fails the
                 guest-choice test at a festival - but this is the one step where
                 nothing is timed, so a keyboard is survivable for the people who
                 want it. It must never block anyone who does not. */
              <button type="button" onClick={() => { buzz(8); setShowDetail(true); }}
                      className="rf-ink w-full text-center py-3"
                      style={{ color: 'rgba(247,228,187,.55)', fontSize: '.95rem' }}>
                + Add a detail (optional)
              </button>
            )
          }
        >
          {VISIBLE_STYLES.map((st) => (
            <button key={st.v} type="button" className="rf-pick"
                    style={{ minHeight: 92, gap: '.15rem' }}
                    onClick={() => { buzz(10); setLikeness(st.v); setStep('ready'); }}>
              <span style={{ fontSize: '1.9rem', lineHeight: 1 }}>{st.e}</span>
              {st.n}
              {/* ⚠️ 80% AND .88rem ARE NOT A TASTE CHOICE. This is an outdoor booth in
                  Kansas in September; cream at 55% on this ground is unreadable in
                  direct sun, which quietly made the descriptions decorative. If you
                  dim these again, the second line stops being read at all. */}
              <span className="rf-ink" style={{ color: 'rgba(247,228,187,.80)', fontSize: '.88rem' }}>{st.d}</span>
            </button>
          ))}
        </Step>
      )}

      {step === 'ready' && (
        <Step
          title={isGroup ? `Person 1 of ${people}` : 'Ready?'}
          /* ⚠️ "AT EYE LEVEL" IS NOT DECORATION. A short guest looking up at a
             phone held at adult height gives us a steep upward angle: nostrils
             forward, chin foreshortened, forehead looming. The model is
             faithful and paints exactly that. The prompt now corrects for it
             too (see LENS in the generator), but a good frame beats a repair. */
          sub={camBlocked
            ? 'Nothing is coming through. Try the retry button below.'
            : (sharp < BLUR_MIN
              ? 'Hold still - it looks blurry'
              : 'Hold the glass at eye level and fill the ring')}
          dots={DOTS} dotIndex={(isGroup ? 4 : 3) - (FORCED_STYLE ? 1 : 0)}
          onBack={() => setStep(FORCED_STYLE ? 'scene' : 'style')}
          footer={
            <>
              {camError && (
                <div className="mb-3 rounded-xl border border-red-500/40 bg-red-500/10 p-3">
                  <div className="flex gap-2">
                    <AlertTriangle size={15} className="text-red-400 shrink-0 mt-0.5" />
                    <span className="rf-plain text-xs text-red-300 break-words">{camError}</span>
                  </div>
                  {/* ⚠️ A RETRY THE GUEST CAN ACTUALLY REACH. Before this, a
                      black viewfinder had exactly one escape: notice it, guess
                      the cause, and reload the page — which loses the session.
                      `startCamera` stops any existing tracks first, so this
                      genuinely re-acquires rather than re-asking a dead stream.
                      Found 2026-08-27 when a real black-camera failure left a
                      guest with nothing to press. */}
                  <button type="button"
                          onClick={() => { buzz(10); startCamera(); }}
                          className="rf-chip w-full justify-center mt-2"
                          style={{ minHeight: 48 }}>
                    <RotateCcw size={16} className="inline mr-2" /> Retry the camera
                  </button>
                </div>
              )}
              {/* ⚠️ HapticButton, not <button>, so an iPhone actually buzzes.
                  On every other device it renders exactly this markup and
                  behaves identically — see boothCues.jsx for why the trick is
                  limited to direct taps and can never reach the countdown. */}
              <HapticButton onPress={beginCapture} label={isGroup ? 'Begin' : "I'm ready"}
                            className="rf-big w-full rounded-2xl px-4 py-5"
                            style={{ fontSize: '1.6rem' }}>
                <Camera size={26} className="inline mr-2" />
                {isGroup ? 'Begin' : "I'm ready"}
              </HapticButton>
            </>
          }
        >
          <div className="rf-fill">{viewfinder}</div>
        </Step>
      )}

      {step === 'capturing' && (
        <Step
          /* ⚠️ THE BLURRY FRAME IS THE BACKGROUND, NOT A THUMBNAIL.
             In a group the question is not "was shot 3 soft" - it is "who
             moved". Nobody can answer that from a 54px strip. Showing the
             rejected photograph full-bleed behind the question lets the group
             point at the person themselves, which is faster and funnier than
             any wording we could write. */
          footer={retake ? (
            <>
              <div className="flex gap-3">
                <HapticButton onPress={() => answerRetake(true)} label="Retake"
                              className="rf-big flex-1 rounded-2xl px-4 py-5"
                              style={{ fontSize: '1.5rem' }}>
                  Retake
                </HapticButton>
                <HapticButton onPress={() => answerRetake(false)} label="Keep it"
                              className="rf-big flex-1 rounded-2xl px-4 py-5"
                              style={{ fontSize: '1.5rem', opacity: 0.75 }}>
                  Keep it
                </HapticButton>
              </div>
              <p className="rf-ink text-center mt-2"
                 style={{ color: 'rgba(247,228,187,.55)', fontSize: '.76rem' }}>
                One retake, then we paint. You can keep it if you like it.
              </p>
            </>
          ) : waitingFor > 0 ? (
            <>
              {/* ⚠️ THE CONSENT LINE LIVES HERE, not only on a splash screen.
                  One guest tapping agree for a crowd is not consent for the
                  crowd. This is the only moment each person is individually
                  present, so it is the only honest place to ask them. */}
              {/* ⚠️ A POINTER, BECAUSE THE PROBLEM WAS NEVER THAT THE BUTTON WAS
                  TOO SMALL. It is full width and 1.5rem. People missed it
                  because nothing connected the instruction at the top of the
                  screen to the control at the bottom, and because "Person 2 is
                  ready" reads as a STATUS someone else will act on rather than
                  a thing to press. The arrow closes that gap physically and the
                  verb closes it grammatically. */}
              <div aria-hidden="true" className="rf-nudge"
                   style={{ display: 'flex', justifyContent: 'center', marginBottom: '.15rem' }}>
                <ChevronDown size={30} strokeWidth={3} style={{ color: 'rgba(247,228,187,.85)' }} />
              </div>
              <HapticButton onPress={iAmReady} label={`Tap when Person ${waitingFor} is ready`}
                            className="rf-big rf-cue w-full rounded-2xl px-4 py-5"
                            style={{ fontSize: '1.5rem' }}>
                Tap when Person {waitingFor} is ready
              </HapticButton>
              <p className="rf-ink text-center mt-2" style={{ color: 'rgba(247,228,187,.55)', fontSize: '.76rem' }}>
                By tapping, this person agrees to their photo being used for the portrait.
              </p>
            </>
          ) : null}
        >
          {retake ? (
            <div className="rf-fill" style={{ position: 'relative' }}>
              <img src={retake.url} alt=""
                   style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16 }} />
              <div style={{
                position: 'absolute', left: 0, right: 0, bottom: 0, padding: '1.25rem 1rem',
                background: 'linear-gradient(to top, rgba(0,0,0,.82), rgba(0,0,0,0))',
                borderRadius: '0 0 16px 16px',
              }}>
                <p className="rf-big" style={{ fontSize: '1.7rem', lineHeight: 1.1, color: '#f7e4bb' }}>
                  {retake.label} looks blurry
                </p>
                <p className="rf-ink" style={{ color: 'rgba(247,228,187,.7)', fontSize: '.9rem', marginTop: '.25rem' }}>
                  Take it again?
                </p>
              </div>
            </div>
          ) : (
            <div className="rf-fill">{viewfinder}</div>
          )}
          {!retake && frames.length > 0 && (
            <div className="flex gap-2 justify-center shrink-0">
              {frames.map((f, i) => (
                <img key={i} src={f} alt="" className="rounded-lg rf-rise"
                     style={{ width: 54, aspectRatio: '3/4', objectFit: 'cover' }} />
              ))}
            </div>
          )}
        </Step>
      )}

      {/* ══ PAINTING — the wait is the show ═══════════════════════════ */}
      {step === 'painting' && (
        <Step
          footer={onShop ? (
            /* ⚠️ SHOP ACCESS LIVES HERE NOW, not in a nav bar overhead.
               The bar was costing 60px of viewfinder on every camera screen, and
               the only moment a guest actually wants the shop is while they are
               standing still waiting. This is a VIEW SWITCH, never a route - the
               poll must keep running or the portrait is lost. */
            <button type="button" onClick={() => { buzz(10); onShop(); }}
                    className="rf-chip w-full justify-center" style={{ minHeight: 52 }}>
              Browse the Shoppe while you wait
            </button>
          ) : null}
        >
          <div className="rf-fill relative rounded-2xl overflow-hidden bg-black">
            {partial.url
              ? <img key={partial.index} src={partial.url} alt="" className="w-full h-full object-contain rf-rise" />
              : frames.length > 0 && (
                <img key={bgIdx} src={frames[bgIdx % frames.length]} alt=""
                     className="absolute inset-0 w-full h-full object-cover rf-fade"
                     style={{ opacity: .45, filter: 'sepia(.35) contrast(1.05)' }} />
              )}

            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="rf-sweep absolute top-0 bottom-0 w-1/3"
                   style={{ background: 'linear-gradient(90deg, transparent, rgba(255,214,120,.24), transparent)' }} />
            </div>

            {/* ═══ THE WAIT SCREEN ═══════════════════════════════════════
                ⚠️ THE COPY IS ON THE PICTURE NOW, AND THE OLD RULE STILL HOLDS.
                This block used to carry: "ALL copy in a solid band. Never over
                the bare image - the thing behind it is a painting in colours we
                do not control." That scar is real and it is not repealed. Joel
                asked for the messages to sit "on top of the photo where the
                countdown is" rather than in a band underneath, and both are
                satisfiable: the text moves onto the picture INSIDE ITS OWN
                SCRIM. Never put a word on bare artwork. A plate is the whole
                reason this is allowed.

                ⚠️ THE PLATE SHRINKS THE MOMENT REAL BRUSHSTROKES ARRIVE. Before
                the first partial there is nothing to look at, so the plate is
                large and central and carries the countdown. After it, the
                picture IS the progress indicator and a card sitting on the
                guest's own face is vandalism. It becomes a pill at the top. */}

            {/* ⚠️ THE PLATE SITS IN THE LOWER THIRD, NOT THE MIDDLE.
                Dead centre put it directly over the guest's face — the camera
                frames a head in the upper-middle, so "centred" and "over the
                nose" are the same place. Joel, watching a real run 2026-08-27:
                "The countdown now hovers on top of the face of the human ...
                people are going to want to look at their face."

                68% keeps the upper two thirds clear for the face while the
                plate still reads as attached to the picture rather than
                floating under it. Once a partial arrives it becomes a pill at
                the top, where it covers the frame's banner and not a face. */}
            <div className="absolute inset-x-0 flex justify-center pointer-events-none"
                 style={{
                   top: partial.url ? 12 : '68%',
                   transform: partial.url ? 'none' : 'translateY(-50%)',
                   transition: 'top .5s ease, transform .5s ease',
                   padding: '0 12px',
                 }}>
              <div className="rf-rise" style={{
                background: 'rgba(24,17,10,.88)',
                border: '1px solid rgba(247,228,187,.18)',
                borderRadius: partial.url ? 999 : 20,
                padding: partial.url ? '.5rem 1rem' : '1.1rem 1.4rem',
                maxWidth: '92%',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(0,0,0,.45)',
                transition: 'all .5s ease',
              }}>
                {!partial.url && (
                  <div className="relative mx-auto" style={{ width: 124, height: 124 }}>
                    <svg viewBox="0 0 120 120" className="w-full h-full" style={{ transform: 'rotate(-90deg)' }}>
                      <circle cx="60" cy="60" r={R} fill="none" strokeWidth="7" stroke="rgba(0,0,0,.45)" />
                      <circle cx="60" cy="60" r={R} fill="none" strokeWidth="7" strokeLinecap="round" stroke={GOLD}
                              strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - ringPct)}
                              style={{ transition: 'stroke-dashoffset .4s linear' }} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="rf-ink text-5xl leading-none"
                            style={{ color: GOLD, textShadow: '0 2px 8px rgba(0,0,0,.8)' }}>{remaining}</span>
                      <span className="rf-ink text-[.7rem] mt-1" style={{ color: '#f6ecd8', opacity: .75 }}>seconds</span>
                    </div>
                  </div>
                )}

                {/* The saying. This is the thing Joel asked to move onto the
                    photo, and it is the reason a guest looks at the screen for
                    a minute instead of at their friend. */}
                <p className="rf-ink"
                   style={{
                     color: GOLD, margin: partial.url ? 0 : '.7rem 0 0',
                     fontSize: partial.url ? '1rem' : '1.35rem', lineHeight: 1.25,
                     textShadow: '0 2px 8px rgba(0,0,0,.9)',
                   }}>
                  {status || patter}
                </p>

                {/* ⚠️ PRIZES GO HERE, NOT IN A POPUP. Joel, 2026-08-27: the
                    winning "should be things that are happening while they are
                    watching their photo be made". A modal would cover the
                    portrait; this sits in the plate that is already there.
                    Currently `prizeMessage` is always null - the slot is wired
                    and the rules are not written yet (Q-25). It renders nothing
                    until it has something true to say. */}
                {prize && prizeShown && (
                  <div className="rf-rise" style={{
                    marginTop: '.6rem', paddingTop: '.6rem',
                    borderTop: '1px solid rgba(247,228,187,.2)',
                  }}>
                    <p className="rf-ink" style={{
                      margin: 0, fontSize: '.75rem', letterSpacing: '.18em',
                      color: 'rgba(247,228,187,.65)',
                    }}>
                      YOU WON
                    </p>
                    <p className="rf-plain rf-halo" style={{
                      margin: '.1rem 0 0', fontSize: partial.url ? '.95rem' : '1.15rem',
                      fontWeight: 800, color: '#fff3d0',
                      textShadow: '0 2px 10px rgba(0,0,0,.9)',
                    }}>
                      {prize.label}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* ═══ the status bar ════════════════════════════════════════
                ⚠️ IT SHOWS A NUMBER BECAUSE A BAR ALONE ANSWERS THE WRONG
                QUESTION. A guest standing in a field wants "how much longer",
                and a gold sliver one pixel tall does not say. Joel asked for
                "a status bar saying where the photo is at and what percentage".

                ⚠️ IT NEVER REACHES 100 AND NEVER GOES BACKWARDS. `ringPct` is
                clamped at 0.97 upstream, deliberately: a bar that reads 100%
                while the screen is still empty is a bar the guest stops
                believing, and everything after that moment feels broken even
                when it is working. The last 3% belongs to the picture arriving. */}
            <div className="absolute bottom-0 left-0 right-0"
                 style={{ background: 'rgba(24,17,10,.93)', color: '#f6ecd8' }}>
              <div className="px-4 pt-2.5 pb-2 flex items-baseline gap-3">
                <span className="rf-plain text-sm opacity-80 min-w-0 flex-1 truncate">
                  {partial.count
                    ? `Pass ${partial.count} of ${PARTIAL_TARGET} · painted before your eyes`
                    : (THEME?.prepLine || `${PAINTER.name} prepares his panel`)}
                </span>
                <span className="rf-plain shrink-0" style={{ color: GOLD, fontSize: '1.05rem', fontWeight: 700 }}>
                  {Math.round(ringPct * 100)}%
                </span>
              </div>
              <div style={{ height: 6, background: 'rgba(0,0,0,.45)' }}>
                <div style={{
                  height: '100%', width: `${ringPct * 100}%`, background: GOLD,
                  transition: 'width .4s linear',
                  boxShadow: '0 0 10px rgba(255,214,120,.6)',
                }} />
              </div>
            </div>
          </div>
        </Step>
      )}

      {/* ═══ the bake-off, blind then revealed (D-45) ═══════════════════
          ⚠️ NO NAMES, NO SECONDS, NO PRICES until a pick is made. Knowing which
          one cost 3.4x the others is exactly the fact that stops you seeing the
          picture, and the prices are already written down. The only thing this
          screen can add is which portrait a person actually prefers - and it
          can only add it once. */}
      {step === 'compare' && (
        <Step
          title={revealed ? 'What each painter did' : 'Which one is best?'}
          sub={revealed
            ? 'Same three photographs, same prompt. Only the painter changed.'
            : 'Same three photographs. Tap one to see it big, then choose.'}
          footer={
            <button type="button" onClick={startOver}
                    className="rf-chip w-full justify-center" style={{ minHeight: 52 }}>
              <Crown size={16} className="inline mr-2" /> Run another test
            </button>
          }
        >
          {/* ⚠️ NOT `rf-fill`. It is `display:flex` with `.rf-fill > * { max-height:100% }`,
              so a grid placed inside it cannot grow past the viewport: the cards got
              squashed to the height of their images and every caption underneath was
              clipped away by the card's own `overflow:hidden`. The reveal WAS rendering
              - into a region the layout had cut off. Found 2026-08-23 from a screenshot
              showing four portraits and not one word of text (`R-31`). */}
          <div style={{ flex: 1, minHeight: 0, width: '100%', overflowY: 'auto', paddingBottom: '.5rem' }}>
            <div style={{
              display: 'grid', gap: '.6rem',
              gridTemplateColumns: testRuns.length > 2 ? '1fr 1fr' : '1fr',
            }}>
              {testRuns.map((r, i) => {
                const row = r.row || {};
                const ok = row.status === 'done' && row.image_url;
                const isWinner = revealed && winner === r.job_id;
                return (
                  <div key={r.job_id}
                       style={{
                         border: `${isWinner ? 2 : 1}px solid ${isWinner ? GOLD : 'rgba(247,228,187,.18)'}`,
                         borderRadius: 14, background: '#000',
                         /* height from content, never from the row - the caption must
                            always have somewhere to be. */
                         alignSelf: 'start',
                       }}>
                    <div style={{ position: 'relative', aspectRatio: '4 / 5', overflow: 'hidden', borderRadius: '13px 13px 0 0' }}>
                      {ok ? (
                        <img src={row.image_url} alt={revealed ? row.model_label : `Portrait ${LETTERS[i]}`}
                             onClick={() => setZoom(row.image_url)}
                             style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <div className="rf-ink" style={{
                          display: 'grid', placeItems: 'center', height: '100%', padding: '.8rem',
                          textAlign: 'center', fontSize: '.75rem', color: '#f6a6a6',
                        }}>
                          {row.status === 'blocked'
                            ? (BLOCK_MESSAGES[row.block_reason] || 'Refused')
                            : (row.error || (row.status === 'running' ? 'Still painting...' : 'No picture'))}
                        </div>
                      )}
                      <span className="rf-ink" style={{
                        position: 'absolute', top: 6, left: 6, background: 'rgba(0,0,0,.7)',
                        borderRadius: 6, padding: '.05rem .4rem', fontWeight: 700, fontSize: '.85rem',
                      }}>{LETTERS[i]}</span>
                    </div>

                    <div style={{ padding: '.45rem .55rem' }}>
                      {revealed ? (
                        <div className="rf-ink" style={{ fontSize: '.72rem', lineHeight: 1.5 }}>
                          <div style={{ fontWeight: 700 }}>
                            {row.model_label || r.model_id}{isWinner ? ' - your pick' : ''}
                          </div>
                          <div style={{ opacity: .65 }}>
                            {manyVariants ? `frames ${r.variant} - ` : ''}{secs(row.generation_ms)} - {usd(row.cost_usd)}
                          </div>
                        </div>
                      ) : (
                        <button type="button" disabled={!ok}
                                onClick={() => pickWinner(r.job_id)}
                                className="rf-chip w-full justify-center"
                                style={{ minHeight: 40, opacity: ok ? 1 : .4 }}>
                          This one
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {!revealed && (
              <button type="button" onClick={() => setRevealed(true)}
                      className="rf-chip w-full justify-center mt-3" style={{ minHeight: 44 }}>
                Show me which is which
              </button>
            )}

            {/* ⚠️ THE ANSWER GOES IN ONE PLACE, NOT SPRINKLED UNDER FOUR PICTURES.
                Captions under a two-up grid are unreadable as a comparison - you
                cannot line up four times and four prices by scrolling between them.
                Fastest first, because speed is the thing a queue feels. */}
            {revealed && (
              <div style={{
                marginTop: '.8rem', border: '1px solid rgba(247,228,187,.22)',
                borderRadius: 12, overflow: 'hidden',
              }}>
                <table className="rf-ink" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
                  <thead>
                    <tr style={{ background: 'rgba(0,0,0,.35)' }}>
                      <th style={{ textAlign: 'left', padding: '.4rem .5rem', fontWeight: 600, opacity: .6 }}>Painter</th>
                      {manyVariants && (
                        <th style={{ textAlign: 'left', padding: '.4rem .35rem', fontWeight: 600, opacity: .6 }}>Frames</th>
                      )}
                      <th style={{ textAlign: 'right', padding: '.4rem .5rem', fontWeight: 600, opacity: .6 }}>Paint</th>
                      <th style={{ textAlign: 'right', padding: '.4rem .5rem', fontWeight: 600, opacity: .6 }}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testRuns
                      .slice()
                      .sort((a, b) => (a.row?.generation_ms ?? 1e9) - (b.row?.generation_ms ?? 1e9))
                      .map((r) => {
                        const row = r.row || {};
                        /* ⚠️ SAY WHEN A PRICE IS A GUESS. Gemini returns no token
                           counts, so its cost is a flat table estimate while the
                           OpenAI numbers are measured from real tokens. Printing
                           both the same way would quietly turn an assumption into
                           a finding. */
                        const est = typeof row.cost_basis === 'string' && row.cost_basis.startsWith('flat');
                        /* ⚠️ THE PICK IS A CELL, NOT A PAINTER. In a matrix the same
                           painter appears four times on different frame subsets, and
                           marking all four would hide the answer the test exists for. */
                        const mine = winner === r.job_id;
                        return (
                          <tr key={r.job_id} style={{ borderTop: '1px solid rgba(247,228,187,.12)' }}>
                            <td style={{ padding: '.4rem .5rem', fontWeight: mine ? 700 : 400, color: mine ? GOLD : undefined }}>
                              {row.model_label || r.model_id}{mine ? ' · your pick' : ''}
                            </td>
                            {manyVariants && (
                              <td style={{ padding: '.4rem .35rem', opacity: .8 }}>{r.variant}</td>
                            )}
                            <td style={{ padding: '.4rem .5rem', textAlign: 'right' }}>{secs(row.generation_ms)}</td>
                            <td style={{ padding: '.4rem .5rem', textAlign: 'right' }}>
                              {est ? '~' : ''}{usd(row.cost_usd)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <div style={{ padding: '.4rem .5rem', fontSize: '.68rem', color: 'rgba(247,228,187,.5)' }}>
                  ~ is a list-price estimate — that provider returned no token counts. The rest
                  are measured from the actual bill.
                </div>
              </div>
            )}
          </div>
        </Step>
      )}

      {step === 'compare' && zoom && (
        <button type="button" onClick={() => setZoom(null)}
                style={{
                  position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.94)',
                  border: 0, padding: '.75rem',
                }}>
          <img src={zoom} alt="Portrait, full size"
               style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </button>
      )}

      {step === 'done' && result && (
        <Step
          footer={
            <>
              {/* ⚠️ DOWNLOAD IS GONE, AND ITS OWN COMMENT SAID WHEN TO DO THIS.
                  The old note read: "Delete this block the day after the first
                  real send lands." Two sends have landed (`ETMVG3` to a primary
                  inbox, `C752K5`), so the condition it set for itself is met.

                  Two reasons, and the second is the real one:
                  · `Save it` handed over the UNFRAMED portrait. The product is
                    the framed composite, which only exists server-side.
                  · It captured nothing. Joel, 2026-08-27: "we're not going to
                    offer it to customers until they get the email." The address
                    IS the business; a free download is the whole lead traded for
                    a tap.

                  ⚠️ WHICH MAKES `resend` IN /api/health LOAD-BEARING NOW.
                  It was already true that email is the only free delivery. With
                  this block gone there is no longer any fallback at all, so that
                  flag going false means the booth photographs people and keeps
                  every picture. Check it before opening, every day. */}
              <EmailCapture onSend={async (email, optIn) => { await sendPhoto(email, optIn); setEmailSent(true); }} />

              {/* ⚠️ THE ONLY ASK FOR MONEY IN THE WHOLE FLOW, AND IT IS BELOW
                  THE ADDRESS ON PURPOSE. Same gate as Share, for a reason the
                  block above already states: this screen's one job is getting
                  the address, and an ask for money competing with it costs the
                  thing that pays for everything. Its own copy makes the order
                  load-bearing too — "your portrait is yours to keep" is only
                  true once it has been sent.

                  ⚠️ ABOVE SHARE, THOUGH, NOT BELOW IT. Share navigates into the
                  OS sheet and a fair number of guests do not come back. The
                  paid action goes first; the free one is still right there.

                  ⚠️ IT RENDERS NOTHING UNTIL THE SERVER QUOTES A PRICE, so a
                  slow tent connection shows this screen exactly as it was
                  before rather than a placeholder. See printUpgrade.jsx. */}
              {emailSent && (
                <PrintUpgrade
                  jobId={result.job_id}
                  getOffer={api.renfestSelfie.printOffer}
                  startCheckout={api.renfestSelfie.printCheckout}
                />
              )}

              {/* ⚠️ SHARE APPEARS ONLY AFTER THE SEND SUCCEEDS. Before that it
                  is a way to leave with the picture and without giving us the
                  address, which is the one thing this screen exists to collect.
                  It still hands over the unframed portrait — pointing it at the
                  framed composite is the open decision, not this gate. */}
              {emailSent && shareable && (
                <div className="flex mt-2" style={{ opacity: .85 }}>
                  <button type="button" onClick={() => doSave('share')}
                          className="rf-chip flex-1 justify-center" style={{ minHeight: 48 }}>
                    <Share2 size={18} className="inline mr-2" />
                    {saving === 'working' ? 'Preparing...' : saving || 'Share it'}
                  </button>
                </div>
              )}

              {/* ⚠️ "PAINT ANOTHER" IS GONE, AND IT CONTRADICTED THE BUSINESS.
                  It offered an unlimited free portrait on the one screen where
                  the model is first-selfie-free. Joel said it twice; the second
                  time settled the entanglement too: the `2 FREE Selfie Credits`
                  prize cannot be a button every guest can press. A credit is
                  GRANTED server-side against a run and SPENT — a guest can no
                  more click their way to one than they can click their way to a
                  discount. `startOver` stays, used by the bake-off in test mode.

                  ⚠️ THIS SCREEN NOW HAS ONE JOB: get the address, then let them
                  share. Nothing else belongs on it. */}
            </>
          }
        >
          {/* flex:1 + min-height:0 so the portrait shrinks to whatever is left
              after the stars and the buttons, instead of pushing them off. */}
          {/* ⚠️ FRAMED IF WE HAVE IT, CLEAN UNTIL THEN. The framed composite is
              the product and the thing the email delivers, so it is what belongs
              on this screen. The fallback is not a degraded state — for the
              first few seconds it is the ONLY thing that exists. `key` forces a
              real element swap so the fade plays on the change. */}
          <div className="rf-fill items-center justify-center">
            <img key={result.framed_url ? 'framed' : 'clean'}
                 src={result.framed_url || result.image_url} alt="Your portrait"
                 className="rounded-2xl rf-rise" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
          </div>

          {/* ⚠️ THE PHOTO ID, AND IT IS ON SCREEN BECAUSE OF WHAT HAPPENS AT THE
              COUNTER. A guest who wants this printed walks to booth #383 and has
              to identify themselves somehow. `job_id` is unreadable aloud and
              `delivery_token` is a credential nobody should say in a crowd, so
              six characters it is (sql/033).

              rf-plain, spaced, and large: a staffer is typing what the guest is
              SAYING across a noisy field. The alphabet already excludes O, I, L,
              0 and 1 — the letterforms do the rest. */}
          {/* ⚠️ THE PRIZE IS REPEATED HERE, and repetition is the feature. The
              wait screen showed it while the portrait painted — which is
              exactly when a guest is looking at the picture and not the words.
              This is the screen they are still holding when they decide whether
              to walk to booth #383, so this is where the offer has to be. */}
          {(result.prize || prize) && (
            <div className="shrink-0 text-center" style={{
              paddingTop: '.4rem', marginTop: '.3rem',
            }}>
              <span className="rf-ink" style={{ fontSize: '.72rem', letterSpacing: '.18em', color: 'rgba(247,228,187,.55)' }}>
                YOU WON
              </span>
              <p className="rf-plain" style={{
                margin: '.05rem 0 0', fontSize: '1.1rem', fontWeight: 800, color: '#fff3d0',
              }}>
                {(result.prize || prize).label}
              </p>
              {/* ⚠️ "SELFIE ID", NOT "PHOTO ID". The prize on this same screen is
                  often a PHOTO product — "85% OFF a THC Drink" sits inches from
                  "photo ID" and a guest reads the two as related. Joel,
                  2026-08-27: "The Photo ID standing for the code below is
                  confusing ... Clashes." One name for this code everywhere: the
                  booth, the delivery page, the email and the counter. */}
              {/* ⚠️ THE CODE THEY ACTUALLY REDEEM, ON THE SCREEN THEY ARE
                  HOLDING. Joel, 2026-09-05: "The user should see it on their
                  screen ... those should be apparent for them to use."

                  It was in the email and on the counter's lookup and nowhere
                  the guest could see while standing at the booth, because
                  prizeFor never selected pos_code. A guest reading "show your
                  Selfie ID" with no code in sight has to trust that something
                  exists; this shows them the thing.

                  ⚠️ THE SELFIE ID STILL LEADS. The code is the offer, the
                  Selfie ID is how the counter finds THEM, and staff ask for the
                  Selfie ID. So this sits above the ID block and is deliberately
                  the quieter of the two. */}
              {(result.prize || prize).pos_code && (
                <p className="rf-plain" style={{
                  margin: '.35rem 0 0', fontSize: '1rem', fontWeight: 800,
                  color: '#fff3d0', letterSpacing: '.12em',
                  fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
                }}>
                  {(result.prize || prize).pos_code}
                </p>
              )}
              <p className="rf-ink" style={{ margin: '.2rem 0 0', fontSize: '.95rem', color: 'rgba(247,228,187,.75)' }}>
                Show your Selfie ID at the booth to claim it
              </p>
              {/* Conditions, where the offer is, rather than only in the email.
                  Small on purpose: it must be readable, not loud. */}
              {(result.prize || prize).terms && (
                <p className="rf-ink" style={{
                  margin: '.25rem auto 0', maxWidth: '30ch', fontSize: '.72rem',
                  lineHeight: 1.35, color: 'rgba(247,228,187,.5)',
                }}>
                  {(result.prize || prize).terms}
                </p>
              )}
            </div>
          )}

          {/* ⚠️ READ BY SOMEONE OLDER THAN US, IN SUNLIGHT, AT ARM'S LENGTH,
              AND THEN SAID ALOUD ACROSS A NOISY COUNTER. Joel, 2026-08-27:
              "That identifier should at least be bolded. Hard to read. Old
              people need the words bigger."

              So: the label is legible rather than a whisper, and the code is
              the largest thing in this block — 800 weight, wide tracking, in a
              boxed panel so the eye lands on it without hunting. The alphabet
              already excludes O, I, L, 0 and 1; the letterforms do the rest. */}
          {result.claim_code && THEME?.claimCode !== false && (
            <div className="shrink-0 text-center" style={{ paddingTop: '.3rem' }}>
              <p className="rf-ink" style={{ color: 'rgba(247,228,187,.75)', fontSize: '.95rem', margin: 0 }}>
                Your Selfie ID — say this at the booth
              </p>
              <p className="rf-plain" style={{
                color: GOLD, fontSize: '2.15rem', fontWeight: 800,
                letterSpacing: '.24em', margin: '.25rem 0 0', lineHeight: 1.1,
                textIndent: '.24em',
                background: 'rgba(0,0,0,.3)', border: '1px solid rgba(240,180,41,.35)',
                borderRadius: 12, padding: '.35rem .6rem',
              }}>
                {result.claim_code}
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 shrink-0">
            <span className="rf-ink" style={{ color: 'rgba(247,228,187,.6)' }}>Any good?</span>
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => score(n)} aria-label={`${n} of 5`}
                      style={{ minWidth: 40, minHeight: 44 }}>
                <Star size={26} className={n <= rating ? 'text-amber-400 fill-amber-400' : 'text-white/25'} />
              </button>
            ))}
          </div>

          {camError && <p className="text-xs text-red-400 text-center break-words">{camError}</p>}
        </Step>
      )}

      {/* ═══ operator telemetry ════════════════════════════════════════
          ⚠️ THIS SAID "Operator-only telemetry" AND WAS SHOWN TO EVERY GUEST.
          The comment described a restriction the code did not enforce — there
          was no gate of any kind, so a stranger at a fairground read the
          generation time, the first-paint time and, worst of all, WHAT THEIR
          PORTRAIT COST US. Found by Joel on the first live run, 2026-08-27:
          "That doesn't need to be shown to a customer."

          Three separate problems in one strip. Runtime and first-paint invite a
          guest to feel slow-served for a number they cannot influence. Cost is
          commercially ours and nobody else's — it is the margin on a product
          the client is reselling, on a screen the client's customers hold.

          ⚠️ A COMMENT IS NOT A GATE. `hasOperatorKey()` is. It reads the
          operator secret out of sessionStorage (lib/operator.js), which only
          somebody who typed it has, and which dies with the tab.

          Kept rather than deleted because the number it shows — real cost per
          real portrait — is the one figure that tells you whether an event is
          profitable while it is still running. */}
      {step === 'done' && result && hasOperatorKey() && (
        <div className="flex flex-wrap gap-3 justify-center text-xs pb-2" style={{ color: 'rgba(247,228,187,.4)' }}>
          <span><Clock size={11} className="inline mr-1" />{secs(result.generation_ms)}</span>
          {result.first_partial_ms != null && (
            <span><Timer size={11} className="inline mr-1" />first paint {secs(result.first_partial_ms)}</span>
          )}
          <span><DollarSign size={11} className="inline mr-1" />{usd(result.cost_usd ?? estCost)}</span>
          {isGroup && <span><Sparkles size={11} className="inline mr-1" />{people} people</span>}
        </div>
      )}
    </div>
  );
}
